import { action, computed, observe, toJS, observable, makeObservable, runInAction } from 'mobx'
import constants, { selectionTypes, languages, layerTypes, FILTER_SETTINGS } from '../constants'
import _ from 'lodash'
import axios from 'axios'
import api from '../api'
import DataStore from './DataStore'
import SelectionStore from './SelectionStore'
import RegionAnalysis from './RegionAnalysis'
import User from './User'
import { LatLngBounds, Point } from 'leaflet'
import densityLayers from 'densityLayers.js'
import { catchError } from './ErrorStore'
import { downloadKML, getShapePopulationFetchOptions } from 'utils.js'
// import ProcessingTargetGroupAnalysis from './ProcessingTargetGroupAnalysis'
import SavedMarker from './SavedMarker'
import Geopolygon from './Geopolygon'
import i18next from '../i18n'
import TargetGroup from './TargetGroup'
import Boundary from './Boundary'
import Filter from './Filter'
import RightSidebarStore from './RightSidebarStore'
import ManageModalStore from './manageModal/ManageModalStore'

export const dataStore = new DataStore()
export const selectionStore = new SelectionStore()
export const rightSidebarStore = new RightSidebarStore()
export const manageModalStore = new ManageModalStore()

// Root store object, used to manage information
// which is not stored on the server, such as settings,
// loading/processing state of the UI, modals, etc. Also initializes
// the other stores.
class UIStore {
  @observable loading = true
  @observable activeTab = 'poi'
  @observable processing = false
  @observable authenticated = false
  @observable print = false
  @observable email = null
  @observable currentYear = 2016
  @observable baseMap = 'googleHybrid'
  forceRefreshTiles = () => {}
  @observable densityLayer = 'none'
  // for boundary tiles
  @observable boundaryLayer = 'admin_boundaries_2018'
  @observable childrenLayer = 'child_admin_boundaries_2018'
  @observable zipCodeBoundaryLayer = 'zip_code_admin_boundaries'
  @observable geoserverNameSpace = 'popex-admin-boundaries'
  @observable zipCodeChildrenLayer = 'zip_code_children_boundaries'
  @observable boundaryOverlayURL = 'https://geoserver-staging.populationexplorer.com/popex-admin-boundaries/wms'
  @observable showMarkerLabels = true
  @observable zoomToSelected = true
  @observable myDataExpanded = false
  @observable myTargetGroupsExpanded = true
  @observable sharedTargetGroupsExpanded = false
  @observable zoomWhilePreviewing = false
  @observable showHoverTooltip = false
  @observable showIncomeTooltip = false
  @observable showPopulationTooltip = false
  @observable showGenderTooltip = false
  @observable showClickTooltip = false
  @observable showDeletePrompt = false
  @observable activeLang = (
    languages.find(l => localStorage.getItem('activeLang') === l.COUNTRY_CODE) || languages[0]
  )

  @observable statusMessage = null
  @observable statusMessageType = 'info'
  @observable hoverTooltipOptions = {}
  @observable incomeTooltipOptions = {}
  @observable populationTooltipOptions = {}
  @observable genderTooltipOptions = {}
  @observable clickTooltipOptions = {}
  @observable deletePromptOptions = {}
  @observable kmlFormat = false
  @observable kmlAllShapes = true
  @observable boundsTopLeftOffset = new Point(0, 0)
  @observable boundsBottomRightOffset = new Point(0, 0)
  @observable printLeftOffset = new Point(0, 0)
  @observable printRightOffset = new Point(0, 0)
  @observable windowSize = null
  @observable.ref bounds = new LatLngBounds([-65, -65], [65, 65])
  @observable.ref viewport = { center: [0, 0], zoom: 2 }
  @observable runHelpTour = false
  @observable dataUpdateInfoModal = false
  @observable isBarChartActive = true
  @observable foundLocation = null
  @observable areMultipolygonsValid = true
  @observable toolbarSelectedMenu = null
  @observable sideMenuHidden = false
  @observable isHideMenuBtnVisible = true
  @observable isHeatmapSettingsVisible = false
  @observable isLayerSettingsVisible = false
  @observable isMapSettingsVisible = false
  @observable isDateYearModalVisible = false
  @observable isAddChoiceTypeVisible = false
  @observable previousSelectionBeforeAdd = null
  @observable isColorPickerExpanded = false
  @observable showColorMenuTransparency = true
  @observable menuWidth = 332
  @observable draggedMenuItem = null
  @observable draggedMenuItemSourceGroup = null
  @observable dragCounter = 0
  @observable isSortPopupVisible = false
  @observable selectedSortType = localStorage.getItem('leftDrawerSortType') || 'ASC'
  @observable isFilterPopupVisible = false
  @observable filters = null
  @observable isRefreshingData = false
  @observable targetGroupsHidden = false
  @observable isFilterActive = false
  @observable isPOIMenuExpanded = false
  @observable skipNextPOIMenuOpen = false
  @observable starterShapesCreated = 0
  @observable poiSearchQuery = ''
  @observable isNearbySearchActive = false // Track if nearby search(without text) was performed
  @observable map = null
  @observable notifications = observable.array()
  @observable hoveredMarkerId = null
  @observable hoveredMarkerIdsForZIndex = observable.map() // Track markers hovered for z-index elevation(key: markerId, value: true)
  @observable clickedMarkerId = null // Track marker whose tooltip is pinned by click
  @observable hoveredPoiMarkerIdsForAnimation = observable.map() // Track markers hovered from POI lists for bounce animation
  @observable hoveredPolygonId = null
  @observable hoveredPolygonGroupId = null
  @observable hoveredPolygonPosition = null // { top, left } relative to map container
  @observable hoveredTargetGroupId = null
  @observable.ref hoveredTargetGroup = null
  @observable hoveredTargetGroupPosition = null // { top, left } relative to map-data container
  layerRefs = {} // Store references to polygon layers for direct manipulation
  @observable showSaveProTooltip = false
  @observable showSaveToFolderProTooltip = false
  @observable isLayerLoaderActive = false
  @observable layerLoaderCount = 0
  @observable isPoiSaving = false
  @observable suppressBoundsUntil = 0
  @observable poiSearchOriginalBounds = null // Store original map bounds when POI search was performed
  @observable showSearchThisAreaButton = false // Whether to show "Search this area" button
  @observable searchButtonWasShown = false // Track if button was shown by user dragging(don't hide on zoom)
  @observable poiIgnoreMoveEndUntil = 0 // Timestamp(ms) to ignore map moveend events after programmatic map moves(e.g. fitBounds after search)
  @observable poiSearchBoundsRectangle = null // Store search bounds rectangle for visualization { sw: {lat, lng}, ne: {lat, lng} }
  _statusMessageTimeout = null

  // SaveModalStore observables
  @observable saveModalIsOpen = false
  @observable saveModalType = null // 'saveChanges', 'saveToFolder', 'upgrade'
  @observable saveModalTitle = ''
  @observable saveModalContent = null
  @observable saveModalOnConfirm = null
  @observable saveModalOnCancel = null
  @observable saveModalConfirmText = 'Confirm'
  @observable saveModalCancelText = 'Cancel'
  @observable saveModalShowCancel = true
  @observable saveModalShowConfirm = true
  @observable isLimitExceeded = false
  @observable limitExceededMessage = ''
  @observable saveModalSelectedOption = 'existing'
  @observable saveModalTargetGroupName = ''
  @observable saveModalSelectedGroup = ''
  @observable saveModalFolderOption = 'existing'
  @observable saveModalNewFolderName = ''
  @observable saveModalSelectedFolder = ''
  @observable saveModalUpgradeFeature = ''
  @observable saveModalUpgradeDescription = ''
  @observable saveModalUpgradeLearnMoreUrl = ''
  @observable saveModalOptions = {}
  @observable dataTypeSelect = [
    { value: 'sum', label: 'Aggregate', isDisabled: false, isLoading: false },
    { value: 'avg', label: 'Average', isDisabled: false, isLoading: false },
    { value: 'dissolved', label: 'Dissolved', isDisabled: true, isLoading: false }
  ]

  @action setActiveTab = (tab) => {
    this.activeTab = tab
  }

  @computed get userTagsSelect () {
    if (Array.isArray(dataStore.userTags)) {
      return dataStore.userTags.map(tag => ({
        key: tag.id,
        color: tag.color,
        value: tag.name,
        label: tag.name
      }))
    }
    return []
  }

  @computed get dataSourceSelect () {
    return [
      { value: 'landscan', label: 'Daytime', isLoading: false, isDisabled: false },
      { value: 'worldpop', label: 'Residential', isLoading: rightSidebarStore.worldpopLoading, isDisabled: !rightSidebarStore.worldpopAvailable }
    ]
  }

  @action setDataTypeSelectOption (option, isLoading, isDisabled) {
    const dataTypeOption = this.dataTypeSelect.find(opt => opt.value === option)
    if (dataTypeOption) {
      dataTypeOption.isLoading = isLoading
      dataTypeOption.isDisabled = isDisabled
    }
  }

  // Quick Create (Right-click on map or existing POIs/markers)
  @observable quickCreate = {
    isOpen: false,
    latlng: null,
    isMenuOpen: false,
    activeModal: null, // null | 'point' | 'buffer' | 'isochrone'
    sourceItem: null // null | marker | poi
  }

  @action openQuickCreate (latlng, sourceItem = null) {
    if (!latlng) return
    if (
      selectionStore.showSubTools ||
      selectionStore.focusedInteraction ||
      selectionStore.isEditing ||
      this.isAddChoiceTypeVisible ||
      this.isAddTargetGroupPaneVisible ||
      selectionStore.type === selectionTypes.NEW_ITEM ||
      selectionStore.type === selectionTypes.NEW_TARGET_GROUP ||
      selectionStore.type === selectionTypes.SHAPE ||
      selectionStore.type === selectionTypes.LINE ||
      selectionStore.type === selectionTypes.POINT_BUFFER ||
      selectionStore.type === selectionTypes.ISOCHRONE ||
      selectionStore.type === selectionTypes.MARKER
    ) {
      return
    }
    selectionStore.hideSubTools()
    selectionStore.hidePopupMenu()
    if (typeof selectionStore.hideContextMenu === 'function') {
      selectionStore.hideContextMenu()
    }
    this.hideColorsMenu()
    this.hideChooseAddType()
    this.hideFilterPopup()
    this.hideSortPopup()
    this.hideHeatmapSettings()
    this.hideLayerSettings()
    this.quickCreate.isOpen = true
    this.quickCreate.latlng = latlng
    this.quickCreate.isMenuOpen = false
    this.quickCreate.activeModal = null
    this.quickCreate.sourceItem = sourceItem
  }

  @action closeQuickCreate () {
    this.quickCreate.isOpen = false
    this.quickCreate.latlng = null
    this.quickCreate.isMenuOpen = false
    this.quickCreate.activeModal = null
    this.quickCreate.sourceItem = null
    this.isColorPickerExpanded = false
  }

  @action toggleQuickCreateMenu () {
    this.quickCreate.isMenuOpen = !this.quickCreate.isMenuOpen
  }

  @action openQuickCreateModal (modalType) {
    this.quickCreate.activeModal = modalType
    this.quickCreate.isMenuOpen = false
  }

  @action closeQuickCreateModal () {
    this.quickCreate.activeModal = null
    this.quickCreate.isOpen = false
    this.quickCreate.latlng = null
    this.isColorPickerExpanded = false
  }

  // Listeners for click events
  clickTooltipClickListener = (event) => {
    const button = document.getElementById('click-tooltip')
    if (!button || (event.target !== button && !button.contains(event.target))) {
      // react-select assigns its "react-select-*" id to the option/control
      // elements themselves, not to every nested child (icons, text spans)
      // a click can land on - walk up the ancestor chain instead of only
      // checking event.target's own id, or clicks inside the dropdown
      // incorrectly count as "outside" and close the tooltip.
      const isReactSelectOption = !!(event.target.closest && event.target.closest('[id*="react-select"]'))
      const isInsideTooltip = !!(event.target.closest && event.target.closest('.click-tooltip'))
      const isMapInteraction = !!(event.target.closest && (
        event.target.closest('.leaflet-container') ||
        event.target.closest('.leaflet-interactive') ||
        event.target.closest('.draw-handle') ||
        event.target.closest('.leaflet-draw') ||
        event.target.closest('.leaflet-marker-icon') ||
        event.target.closest('.leaflet-pane')
      ))
      if (isReactSelectOption || isInsideTooltip || (this.clickTooltipOptions && this.clickTooltipOptions.allowMapClicks && isMapInteraction)) return

      this.hideClickTooltip()
      document.removeEventListener('click', this.clickTooltipClickListener)
    }
  }

  get targetGroupNames () {
    const targetGroups = dataStore.targetGroups || []
    if (!targetGroups.length) {
      return []
    }

    return _.uniq(
      targetGroups
        .map(group => (group && typeof group.name === 'string' ? group.name.trim() : ''))
        .filter(name => name.length > 0)
    )
  }

  @computed get saveModalAvailableGroups () {
    return this.targetGroupNames
  }

  @computed get saveModalAvailableFolders () {
    return this.targetGroupNames
  }

  clickDeletePromptClickListener = (event) => {
    const button = document.getElementById('delete-item-prompt')
    if (!button || (event.target !== button && !button.contains(event.target))) {
      this.hideDeletePrompt()
      document.removeEventListener('click', this.clickDeletePromptClickListener)
    }
  }

  constructor () {
    makeObservable(this)
    // Initialize tooltip styles
    this.clickTooltipOptions.style = {
      display: 'flex',
      width: '220px',
      main: {
        padding: 0
      }
    }
    this.hoverTooltipOptions.style = {
      display: 'flex',
      width: '230px',
      main: {
        padding: 0,
        marginRight: '15px',
        marginLeft: '15px',
      },
    }
    this._defaultHoverTooltipStyle = this.hoverTooltipOptions.style
    this.incomeTooltipOptions.style = {
      display: 'flex',
      width: '350px',
      main: {
        padding: 0,
        marginRight: '15px',
        marginLeft: '15px',
      },
    }
    this.populationTooltipOptions.style = {
      display: 'flex',
      width: '320px',
      main: {
        padding: 0,
        marginRight: '15px',
        marginLeft: '15px',
      },
    }
    this.genderTooltipOptions.style = {
      display: 'flex',
      width: '200px',
      main: {
        padding: 0,
        marginRight: '15px',
        marginLeft: '15px',
      },
    }
    this.deletePromptOptions.style = {
      display: 'flex',
      width: '220px',
      main: {
        padding: 0
      }
    }
    // Close POI tooltips(markers/shapes) and Pro save tooltips when clicking outside
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handlePoiTooltipOutsideClick, true)
    }
    // Deferred via queueMicrotask: rightSidebarStore/selectionStore circularly
    // import back to this module (./UIStore), so observing them synchronously
    // here (while this module is still mid-evaluation) can run before their
    // module finishes initializing. Deferring to a microtask guarantees all
    // store singletons have finished constructing first.
    queueMicrotask(() => {
      observe(rightSidebarStore, 'worldpopAvailable', ({ newValue }) => {
        const worldpopOption = this.dataSourceSelect.find(option => option.value === 'worldpop')
        if (worldpopOption) {
          worldpopOption.isDisabled = !newValue
        }
      })

      observe(rightSidebarStore, 'worldpopLoading', ({ newValue }) => {
        const worldpopOption = this.dataSourceSelect.find(option => option.value === 'worldpop')
        if (worldpopOption) {
          worldpopOption.isLoading = newValue
        }
      })
    })

    queueMicrotask(() => observe(selectionStore, 'item', async ({ newValue }) => {
      // Zoom logic already exists below – this block extends it with POI behaviour
      if (newValue && typeof newValue.bounds === 'function') {
        try {
          // Whenever selection changes away from a saved marker, clear any
          // previously "active" marker state used for z-index and tooltip pinning.
          if (selectionStore.type !== selectionTypes.SAVED_MARKER) {
            this.clearClickedMarker()
            if (this.hoveredMarkerIdsForZIndex && typeof this.hoveredMarkerIdsForZIndex.clear === 'function') {
              this.hoveredMarkerIdsForZIndex.clear()
            }
          }
          const skipMenuOpen = this.skipNextPOIMenuOpen
          if (skipMenuOpen) {
            this.skipNextPOIMenuOpen = false
          }

          // Check if this is a target group selection
          const isTargetGroup = selectionStore.type === selectionTypes.TARGET_GROUP

          if (isTargetGroup) {
            const targetGroup = newValue
            // If POI menu is open, populate it with all saved markers from this target group
            if (!skipMenuOpen && this.isPOIMenuExpanded && targetGroup && dataStore && dataStore.poi) {
              await this.openPoiForTargetGroup(targetGroup)
            } else {
              // Clear POI state when target group is selected but menu is not opened
              if (dataStore && dataStore.poi) {
                dataStore.poi.placeResults = []
                dataStore.poi.temporaryMarkers = []
                dataStore.poi.bucketById.clear()
                dataStore.poi.sharedBucketsById.clear()
                dataStore.poi.pendingBucketById.clear()
                dataStore.poi.disabledRowsById.clear()
                dataStore.poi.removedFromBucketById.clear()
                dataStore.poi.general = []
                dataStore.poi.allies = []
                dataStore.poi.competitors = []
                dataStore.poi.hasBucketChanges = false
                dataStore.poi.currentTargetGroupId = null
              }
            }
          } else if (selectionStore.type === selectionTypes.SAVED_MARKER) {
            // When a marker is clicked, clear POI state but don't open POI menu
            // POI menu should only open via explicit user action(View More button, search input, or poi button)
            if (dataStore && dataStore.poi) {
              dataStore.poi.placeResults = []
              dataStore.poi.temporaryMarkers = []
              dataStore.poi.bucketById.clear()
              dataStore.poi.sharedBucketsById.clear()
              dataStore.poi.pendingBucketById.clear()
              dataStore.poi.disabledRowsById.clear()
              dataStore.poi.removedFromBucketById.clear()
              dataStore.poi.general = []
              dataStore.poi.allies = []
              dataStore.poi.competitors = []
              dataStore.poi.hasBucketChanges = false
              dataStore.poi.currentTargetGroupId = null
            }
            this.isPOIMenuExpanded = false

            // Mark this marker as the active one so its map icon is always
            // rendered above others after zooming from the left/right menus.
            if (newValue && typeof newValue.id !== 'undefined') {
              this.setClickedMarker(newValue.id)
              this.setHoveredMarkerForZIndex(newValue.id)
            }
          } else {
            // For shapes/boundaries: DO NOT auto-call Google Places API.
            // However, when a polygon(or boundary) is clicked, the POI menu
            // should open and show any saved markers that lie inside it.
            const targetGroup = selectionStore && selectionStore.data && selectionStore.data.group
            if (targetGroup && dataStore && dataStore.poi) {
              // Make sure we have the latest saved markers for this group
              await targetGroup.loadSavedMarkers()

              const selectedShape = newValue
              const shapeGeoJSON = selectedShape && selectedShape.geoJSON

              // If we don't have geometry, just clear POI state and exit
              if (!shapeGeoJSON) {
                dataStore.poi.temporaryMarkers = []
                dataStore.poi.placeResults = []
                dataStore.poi.bucketById.clear()
                dataStore.poi.sharedBucketsById.clear()
                dataStore.poi.pendingBucketById.clear()
                dataStore.poi.disabledRowsById.clear()
                dataStore.poi.removedFromBucketById.clear()
                dataStore.poi.general = []
                dataStore.poi.allies = []
                dataStore.poi.competitors = []
                dataStore.poi.hasBucketChanges = false
              } else {
                // Build temporary markers from saved markers that are inside this shape
                const tempMarkers = (targetGroup.savedMarkers || [])
                  .filter(sm => {
                    if (!sm.position || sm.position.latitude == null || sm.position.longitude == null) return false
                    return dataStore.poi.pointInGeoJSON(
                      sm.position.latitude,
                      sm.position.longitude,
                      shapeGeoJSON
                    )
                  })
                  .map(sm => ({
                    id: `saved_${sm.id}`,
                    savedMarkerId: sm.id,
                    position: { latLng: { lat: sm.position.latitude, lng: sm.position.longitude } },
                    name: sm.name || '',
                    address: sm.description || sm.address || '',
                    type: (sm.types && typeof sm.types === 'string') ? sm.types : '',
                    types: sm.types || '',
                    getColor: sm.color_id || 1,
                    color_id: sm.color_id || 1,
                    icon_url: sm.icon_url || null,
                    is_general: sm.is_general,
                    is_ally: sm.is_ally,
                    is_competitor: sm.is_competitor
                  }))

                dataStore.poi.temporaryMarkers = tempMarkers
                dataStore.poi.placeResults = []
                dataStore.poi.currentTargetGroupId = targetGroup.pk
                dataStore.poi.syncBucketsFromSavedMarkers(targetGroup)
              }
              // Do NOT auto-open POI menu on polygon click - only open via "View More" button
            }
          }
        } catch (e) {
          console.warn('POI on select failed:', e)
        }
      }
    }))

    // Initialize save modal options
    this.saveModalOptions.style = {
      display: 'flex',
      width: '300px',
      main: {
        padding: '10px',
        margin: '10px',
      },
    }

    // Observer for selection changes
    // When the selection changes, if the new value
    // implements a `bounds` function and the user has
    // the 'zoom to selected' option enabled, zoom the
    // map to the new selection
    queueMicrotask(() => observe(selectionStore, 'item', ({ newValue }) => {
      if (
        this.zoomToSelected === true &&
        newValue &&
        typeof newValue.bounds === 'function'
      ) {
        if (!selectionStore.menuItem) {
          try {
            const nextBounds = newValue.bounds()
            const leafletBounds = nextBounds && new LatLngBounds().extend(nextBounds)
            if (leafletBounds && leafletBounds.isValid()) {
              this.setBoundsToItem(newValue)
            }
          } catch (e) {
            // Keep the current view if bounds cannot be computed yet
          }
        }
        this.setFoundLocation(null)
      } else if (newValue === null) {
        this.updateBoundsOptions()
      }
    }))

    this.initKeyListeners()
  }

  initKeyListeners () {
    const print = e => {
      this.printScreen()
      e.preventDefault()
    }

    document.addEventListener('keydown', e => {
      if (e.key) {
        const key = e.key.toLowerCase()
        if ((e.ctrlKey || e.metaKey) && key === 'p') {
          return print(e)
        }
      }
    })
  }

  async initializeUser () {
    await this.authenticate()
    if (!this.user) {
      return
    }
    this.checkForInitialHelpTour()
    this.checkForDataUpdateInfoModal()
  }

  @computed get myDisplayGroups () {
    return _.filter(this.displayGroups, { is_owner: true })
  }

  @computed get densityLayerUrl () {
    if (!this.densityLayer || this.densityLayer === 'none') {
      return ''
    }
    const server = densityLayers.server
    const layer = _.find(densityLayers.layers, {
      id: this.densityLayer
    })
    if (!layer || !layer.workspace) {
      return ''
    }
    const workspace = layer.workspace

    const value = this.currentYear
    return value === 2016 ? `${server}wms/${workspace}` : `${server}wms/${workspace}_${value}`
  }

  @computed get allowPanMap () {
    const inputFocus = document.querySelector('input:focus, textarea:focus')
    return !inputFocus && selectionStore.type !== selectionTypes.BOUNDARY_LIST
  }

  @computed get isDensityMapActive () {
    return (this.densityLayer && this.densityLayer !== 'none')
  }

  @computed get quickSettingLayer () {
    if (layerTypes.satelite.includes(this.baseMap)) {
      return 'googleRoadmap'
    } else {
      return 'googleHybrid'
    }
  }

  @computed get quickSettingText () {
    return this.quickSettingLayer === 'googleHybrid' ? 'GLOBAL.SATELITE' : 'GLOBAL.TERRAIN'
  }

  @computed get mapCenterInfo () {
    if (this.map) {
      return this.map.leafletElement.getCenter()
    }
  }

  @computed get mapZoom () {
    if (this.map) {
      return this.map.leafletElement.getZoom()
    }
  }

  @observable processingCount = 0

  @action setProcessing (isProcessing) {
    if (isProcessing) {
      this.processingCount += 1
    } else {
      this.processingCount = Math.max(0, this.processingCount - 1)
    }
    this.processing = this.processingCount > 0
  }

  @action addNotification (notification) {
    if (!notification || !notification.text) return null
    const n = {
      id: notification.id || (Date.now() + Math.random()),
      text: notification.text,
      isError: !!notification.isError,
      isLoading: !!notification.isLoading,
      icon: notification.icon || null,
      category: notification.category || '',
    }

    this.notifications.push(n)

    // Copy/save confirmations need longer than 2s; the folder picker and
    // loader used to hide the toast before it could be read.
    const timeout = typeof notification.timeout === 'number'
      ? notification.timeout
      : (notification.isLoading ? 0 : 4000)
    if (timeout > 0) {
      setTimeout(() => {
        this.clearNotification(n.id)
      }, timeout)
    }
    return n.id
  }

  // Backward-compatible alias for older call sites using the misspelled name.
  @action addNotificatio (notification) {
    this.addNotification(notification)
  }

  @action clearNotification (id) {
    if (typeof id === 'undefined') {
      if (this.notifications.length) this.notifications.shift()
      return
    }

    const idx = this.notifications.findIndex(x => x.id === id)
    if (idx > -1) this.notifications.splice(idx, 1)
  }

  @action.bound setStatusMessage (message, type = 'info', duration = 4000) {
    if (this._statusMessageTimeout) {
      clearTimeout(this._statusMessageTimeout)
    }
    this.statusMessage = message
    this.statusMessageType = type
    if (duration > 0) {
      this._statusMessageTimeout = setTimeout(() => {
        this.clearStatusMessage()
      }, duration)
    }
  }

  @action.bound clearStatusMessage () {
    if (this._statusMessageTimeout) {
      clearTimeout(this._statusMessageTimeout)
      this._statusMessageTimeout = null
    }
    this.statusMessage = null
    this.statusMessageType = 'info'
  }

  setDraggableMenuItem (value) {
    this.draggedMenuItem = value
  }

  setDraggedMenuItemSourceGroup (value) {
    this.draggedMenuItemSourceGroup = value
  }

  resetDragState () {
    this.draggedMenuItemSourceGroup = null
    this.draggedMenuItem = null
    this.dragCounter = 0
  }

  limitErrorMessage () {
    return (
      <>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '5px', fontStyle: 'italic' }}>
          {
            this.user.type === 'standalone'
              ? i18next.t('LIMIT_ERROR_MESSAGE.TITLE_PRO_USER')
              : i18next.t('LIMIT_ERROR_MESSAGE.TITLE_FREE_USER')
          }
        </div>
        <div className='upgrade-account--text'>
          <a className='limit-error-link' href={`${process.env.CORPORATE_URL}/pricing/`}>{i18next.t('LIMIT_ERROR_MESSAGE.BUTTON_TEXT')}</a>
        </div>
      </>
    )
  }

  houseHoldIncome () {
    return (
      <>
        <span>
          <p className='income-title'>
            {i18next.t('INCOME.TITLE')}
          </p>
          <button type='button' className='income-button' onClick={() => this.hideIncomeTooltip()}>&times;</button>
        </span>
        <div className='income-text'>
          {i18next.t('INCOME.TEXT.PART_ONE')} <i>{i18next.t('INCOME.TEXT.PART_TWO')}.</i> {i18next.t('INCOME.TEXT.PART_THREE')}
        </div>
      </>
    )
  }

  population () {
    return (
      <>
        <span>
          <button type='button' className='income-button' onClick={() => this.hidePopulationTooltip()}>&times;</button>
        </span>
        <div className='income-text'>
          <span>
            {i18next.t('POPULATION_TOOLTIP.PART_ONE')} <a style={{ color: '#0099FF' }} target='_blank' onClick={() => window.open(`${process.env.CORPORATE_URL}/resources/data/`, '_blank')}>{i18next.t('POPULATION_TOOLTIP.PART_TWO')}</a>
          </span>
        </div>
      </>
    )
  }

  genderRatio () {
    return (
      <>
        <span>
          <button type='button' className='income-button' onClick={() => this.hideGenderTooltip()}>&times;</button>
        </span>
        <div className='income-text'>
          {i18next.t('GLOBAL.GENDER_RATIO')}
        </div>
      </>
    )
  }

  openClickTooltip (options) {
    this.hideDeletePrompt()
    this.clickTooltipOptions = { position: 'right-start', ...this.clickTooltipOptions, ...options }
    this.showClickTooltip = true
    document.addEventListener('click', this.clickTooltipClickListener)
    this.updateBoundsOptionsHelper()
    const targetItem = (selectionStore && (selectionStore.editableShape || selectionStore.item)) || null
    if (targetItem && typeof this.setBoundsToItem === 'function') {
      this.setBoundsToItem(targetItem)
    }
  }

  hideClickTooltip () {
    this.showClickTooltip = false
    // Drop the outside-click listener so a leftover handler cannot close
    // the success toast that we show right after copy.
    document.removeEventListener('click', this.clickTooltipClickListener)
    this.updateBoundsOptionsHelper()
  }

  openHoverTooltip (options) {
    const style = (options && options.style) || this._defaultHoverTooltipStyle
    this.hoverTooltipOptions = { ...this.hoverTooltipOptions, ...options, style }
    this.showHoverTooltip = true
  }

  hideHoverTooltip () {
    clearTimeout(this._hoverTooltipHideTimer)
    this.showHoverTooltip = false
  }

  scheduleHideHoverTooltip (delay = 250) {
    clearTimeout(this._hoverTooltipHideTimer)
    this._hoverTooltipHideTimer = setTimeout(() => { this.showHoverTooltip = false }, delay)
  }

  cancelHideHoverTooltip () {
    clearTimeout(this._hoverTooltipHideTimer)
  }

  setActiveLang (lang) {
    this.activeLang = lang
  }

  toggleSortPopup () {
    if (this.isSortPopupVisible) {
      this.isSortPopupVisible = false
    } else {
      this.showSortPopup()
    }
  }

  showSortPopup () {
    this.isSortPopupVisible = true
    this.hideFilterPopup()
    this.hideLayerSettings()
    this.hideHeatmapSettings()
    if (selectionStore) selectionStore.hidePopupMenu()
    this.hideClickTooltip()
    this.hideDeletePrompt()
  }

  hideSortPopup () {
    this.isSortPopupVisible = false
  }

  toggleFilterPopup () {
    if (this.isFilterPopupVisible) {
      this.isFilterPopupVisible = false
    } else {
      this.showFilterPopup()
    }
  }

  showFilterPopup () {
    this.isFilterPopupVisible = true
    this.hideSortPopup()
    this.hideLayerSettings()
    this.hideHeatmapSettings()
    if (selectionStore) selectionStore.hidePopupMenu()
    this.hideClickTooltip()
    this.hideDeletePrompt()
  }

  hideFilterPopup () {
    this.isFilterPopupVisible = false
  }

  @action startLayerLoader () {
    this.layerLoaderCount += 1
    this.isLayerLoaderActive = true
  }

  @action stopLayerLoader () {
    this.layerLoaderCount = Math.max(0, this.layerLoaderCount - 1)
    if (this.layerLoaderCount === 0) {
      this.isLayerLoaderActive = false
    }
  }

  @action suppressMapBoundsTemporarily (durationMs = 300) {
    this.suppressBoundsUntil = Date.now() + durationMs
  }

  hideHoverTooltipDelayed () {
    const delay = 5000
    setTimeout(() => {
      this.showHoverTooltip = false
    }, delay)
  }

  @action closeAllHeaderPopups () {
    this.isAddChoiceTypeVisible = false
    this.isFilterPopupVisible = false
    this.isSortPopupVisible = false
    this.toolbarSelectedMenu = null
    this.isLayerSettingsVisible = false
    this.isHeatmapSettingsVisible = false
    this.isMapSettingsVisible = false
    this.isDateYearModalVisible = false
    this.showHoverTooltip = false
    this.showIncomeTooltip = false
    this.showPopulationTooltip = false
    this.showGenderTooltip = false
    if (typeof this.closeQuickCreate === 'function') this.closeQuickCreate()
    if (typeof this.closeQuickCreateModal === 'function') this.closeQuickCreateModal()
    if (typeof this.closePoiTooltip === 'function') this.closePoiTooltip()
    if (this.map && this.map.leafletElement && typeof this.map.leafletElement.closePopup === 'function') {
      this.map.leafletElement.closePopup()
    }
  }

  closeToolbarMenu () {
    this.toolbarSelectedMenu = ''
  }

  setToolbarMenu (toolbarMenu) {
    this.toolbarSelectedMenu = toolbarMenu
  }

  openIncomeTooltip (options) {
    this.incomeTooltipOptions = { ...this.incomeTooltipOptions, ...options }
    this.showIncomeTooltip = true
  }

  openPopulationTooltip (options) {
    this.populationTooltipOptions = { ...this.populationTooltipOptions, ...options }
    this.showPopulationTooltip = true
  }

  openGenderTooltip (options) {
    this.genderTooltipOptions = { ...this.genderTooltipOptions, ...options }
    this.showGenderTooltip = true
  }

  hideIncomeTooltip () {
    this.showIncomeTooltip = false
  }

  hideIncomeTooltipDelayed () {
    const delay = 5000
    setTimeout(() => {
      this.showIncomeTooltip = false
    }, delay)
  }

  hidePopulationTooltip () {
    this.showPopulationTooltip = false
  }

  hidePopulationTooltipDelayed () {
    const delay = 5000
    setTimeout(() => {
      this.showPopulationTooltip = false
    }, delay)
  }

  hideGenderTooltip () {
    this.showGenderTooltip = false
  }

  hideGenderTooltipDelayed () {
    const delay = 5000
    setTimeout(() => {
      this.showGenderTooltip = false
    }, delay)
  }

  openDeletePrompt (options) {
    this.hideClickTooltip()
    this.deletePromptOptions = { position: 'right-start', ...this.deletePromptOptions, ...options }
    this.showDeletePrompt = true
    document.addEventListener('click', this.clickDeletePromptClickListener)
  }

  hideDeletePrompt () {
    this.showDeletePrompt = false
  }

  showHideMenuBtn () {
    this.isHideMenuBtnVisible = true
  }

  hideHideMenuBtn () {
    // Keep collapse button visible per user requirement
    this.isHideMenuBtnVisible = true
  }

  // SaveModalStore actions
  @action saveModalOpenModal (type, options = {}) {
    this.saveModalType = type
    this.saveModalTitle = options.title || i18next.t('MODAL.DEFAULT_TITLE')
    this.saveModalContent = options.content || null
    this.saveModalOnConfirm = options.onConfirm || null
    this.saveModalOnCancel = options.onCancel || null
    this.saveModalConfirmText = options.confirmText || i18next.t('MODAL.CONFIRM')
    this.saveModalCancelText = options.cancelText || i18next.t('MODAL.CANCEL')
    this.saveModalShowCancel = options.showCancel !== false
    this.saveModalShowConfirm = options.showConfirm !== false
    this.saveModalIsOpen = true
  }

  @action saveModalCloseModal () {
    this.saveModalIsOpen = false
    this.saveModalType = null
    this.saveModalTitle = ''
    this.saveModalContent = null
    this.saveModalOnConfirm = null
    this.saveModalOnCancel = null
    this.saveModalResetModalData()
  }

  @action saveModalResetModalData () {
    this.saveModalSelectedOption = 'existing'
    this.saveModalTargetGroupName = ''
    this.saveModalSelectedGroup = ''
    this.saveModalFolderOption = 'existing'
    this.saveModalNewFolderName = ''
    this.saveModalSelectedFolder = ''
    this.saveModalUpgradeFeature = ''
    this.saveModalUpgradeDescription = ''
    this.saveModalUpgradeLearnMoreUrl = ''
  }

  @action async saveModalHandleConfirm () {
    if (this.saveModalOnConfirm) {
      try {
        await this.saveModalOnConfirm()
        // Only close modal after async operation completes successfully
        this.saveModalCloseModal()
      } catch (error) {
        console.error('Error in save modal confirm:', error)
        // Don't close modal on error - let user see error notification and retry
      }
    } else {
      this.saveModalCloseModal()
    }
  }

  @action saveModalHandleCancel () {
    if (this.saveModalOnCancel) {
      this.saveModalOnCancel()
    }
    this.saveModalCloseModal()
  }

  @action saveModalOpenSaveChangesModal (options = {}) {
    this.saveModalOpenModal('saveChanges', {
      title: i18next.t('MODAL.SAVE_CHANGES_TITLE'),
      confirmText: i18next.t('MODAL.CONFIRM'),
      cancelText: i18next.t('MODAL.CANCEL'),
      ...options,
    })
  }

  @action saveModalSetSelectedOption (option) {
    this.saveModalSelectedOption = option
    if (option === 'existing') {
      this.saveModalTargetGroupName = ''
    } else {
      this.saveModalSelectedGroup = ''
    }
  }

  @action saveModalSetTargetGroupName (name) {
    this.saveModalTargetGroupName = name
  }

  @action saveModalSetSelectedGroup (group) {
    this.saveModalSelectedGroup = group
  }

  @action saveModalOpenSaveToFolderModal (options = {}) {
    this.saveModalOpenModal('saveToFolder', {
      title: i18next.t('MODAL.SAVE_TO_FOLDER_TITLE'),
      confirmText: i18next.t('MODAL.CONFIRM'),
      cancelText: i18next.t('MODAL.CANCEL'),
      ...options,
    })
  }

  @action saveModalSetFolderOption (option) {
    this.saveModalFolderOption = option
    if (option === 'existing') {
      this.saveModalNewFolderName = ''
    } else {
      this.saveModalSelectedFolder = ''
    }
  }

  @action saveModalSetNewFolderName (name) {
    this.saveModalNewFolderName = name
  }

  @action saveModalSetSelectedFolder (folder) {
    this.saveModalSelectedFolder = folder
  }

  @action saveModalOpenUpgradeModal (options = {}) {
    this.saveModalUpgradeFeature = options.feature || i18next.t('MODAL.UPGRADE_FEATURE_DEFAULT')
    this.saveModalUpgradeDescription =
      options.description || i18next.t('MODAL.UPGRADE_DESCRIPTION_DEFAULT')
    this.saveModalUpgradeLearnMoreUrl = options.learnMoreUrl || '#'

    this.saveModalOpenModal('upgrade', {
      title: i18next.t('MODAL.UPGRADE_TITLE', { feature: this.saveModalUpgradeFeature }),
      confirmText: i18next.t('MODAL.UPGRADE_CONFIRM'),
      cancelText: i18next.t('MODAL.UPGRADE_LEARN_MORE'),
      showCancel: true,
      showConfirm: true,
      ...options,
    })
  }

  @action saveModalHandleLearnMore () {
    if (this.saveModalUpgradeLearnMoreUrl && this.saveModalUpgradeLearnMoreUrl !== '#') {
      window.open(this.saveModalUpgradeLearnMoreUrl, '_blank')
    }
  }

  @computed get isSaveModalVisible () {
    return this.saveModalIsOpen
  }

  @computed get saveModalConfig () {
    return {
      type: this.saveModalType,
      title: this.saveModalTitle,
      content: this.saveModalContent,
      confirmText: this.saveModalConfirmText,
      cancelText: this.saveModalCancelText,
      showCancel: this.saveModalShowCancel,
      showConfirm: this.saveModalShowConfirm,
      style: this.saveModalOptions.style,
    }
  }

  @catchError
  async exportKml () {
    this.showClickTooltip = false
    const config = { responseType: 'blob' }
    let response
    switch (selectionStore.type) {
      case selectionTypes.SAVED_MARKER:
        response = await api.getMarkerKML(selectionStore.item.id, !this.kmlFormat, config)
        break
      case selectionTypes.GEOPOLYGON:
        response = await api.getRegionKML(selectionStore.item.id, !this.kmlFormat, config)
        break
      case selectionTypes.BOUNDARY:
        response = await api.getBoundaryKML(selectionStore.item.inner_id, !this.kmlFormat, config)
        break
      case selectionTypes.TARGET_GROUP:
        await api.getTargetGroupKML(selectionStore.item.pk, { kmz_file: !this.kmlFormat, all_shapes: this.kmlAllShapes })
        return selectionStore.item.loadKML()
    }
    downloadKML(selectionStore.item.name, response.data, this.kmlFormat)
  }

  @catchError
  async authenticate () {
    const years = await api.getYears()
    dataStore.years = years.data

    // const shapeStatuses = await api.getShapeStatuses()
    // dataStore.shapeStatuses = _.map(shapeStatuses.data, (shape) => {
    //   return {
    //     value: shape.name,
    //     label: shape.name,
    //     color: shape.color
    //   }
    // })

    // const folderStatuses = await api.getFolderStatuses()
    // console.log('fodler status', folderStatuses)
    // dataStore.folderStatuses = _.map(folderStatuses.data, (folder) => {
    //   return {
    //     value: folder.name,
    //     label: folder.name,
    //     color: folder.color
    //   }
    // })

    // const tags = await api.getUserTags()
    // this.userTags = tags

    // remove token

    const response = await api.getUser()

    const groups = await api.getLicenseGroup()
    const group = _.find(groups.data, { is_current: true }) || _.find(groups.data, { get_status_display: 'Trialing' })

    dataStore.shapefileCount = (await api.getUserShapefiles()).data.length
    dataStore.markerfileCount = (await api.getUserMarkerfiles()).data.length

    const defaultYear = await api.getDefaultYear()
    dataStore.defaultYear = defaultYear.data
    // Site load default: LandScan 2024 (WorldPop uses 2024 when selected in People tab / setDataWsParams)
    this.setDataWsParams(2024, 'landscan')

    const filterDefaults = await api.getFilterDefaults()
    this.filters = new Filter(filterDefaults)

    const limits = {
      max_target_groups: response.data.max_target_groups,
      max_regions: response.data.max_regions,
      max_shapefiles: response.data.max_shapefiles,
      max_markerfiles: response.data.max_markerfiles,
      max_markers: response.data.max_markers
    }

    const res = await api.getMarkerColors()
    dataStore.markerColors = res.data

    const recentColors = await api.getRecentColors(5)
    dataStore.recentColors = recentColors.data

    if (
      group.get_status_display.toLowerCase() === constants.TRIAL_STATUS ||
      group.get_status_display.toLowerCase() === constants.ENTERPRISE_TRIAL_STATUS ||
      group.get_status_display.toLowerCase() === constants.ACTIVE_STATUS
    ) {
      console.log('response user type', response.data.user_type)
      console.log('respose user plan', response.data.plan)
      this.user = new User({
        id: response.data.user_id,
        type: response.data.user_type,
        email: response.data.email,
        accountStatus: group.get_status_display,
        number_of_licenses: group.number_of_licenses,
        plan: response.data.plan,
        limits,
        is_first_login: response.data.is_first_login,
        is_advertise_popup_visible: response.data.is_advertise_popup_visible,
        limitErrorMessage: response.data.limit_message,
        needs_recalculation: response.data.needs_recalculation,
        user_api_token: response.data.user_api_token,
        region_limit_locked: response.data.region_limit_locked,
      })
      this.starterShapesCreated = response.data.starter_shapes_created || 0
      await this.onAuthenticationComplete()
    }
  }

  setDataWsParams (ds_year, ds_name) {
    const normalizedDsName = ds_name === 'worldpop' ? 'worldpop' : (ds_name || 'landscan')
    const normalizedDsYear = normalizedDsName === 'worldpop' ? 2024 : ds_year
    dataStore.defaultYear.result = normalizedDsYear
    axios.defaults.headers.common['Ds-Year'] = String(normalizedDsYear)
    dataStore.defaultDsName = normalizedDsName
    axios.defaults.headers.common['Ds-Name'] = normalizedDsName
  }

  @catchError
  async resetDataWsParams (ds_year, ds_name) {
    const prevDsName = dataStore.defaultDsName
    const dsNameChanged = ds_name && ds_name !== prevDsName
    this.setDataWsParams(ds_year, ds_name)
    switch (selectionStore.type) {
      case selectionTypes.SAVED_MARKER:
        break
      case selectionTypes.GEOPOLYGON:
        if (typeof selectionStore.item.reloadAnalysisForCurrentYear === 'function' && !dsNameChanged) {
          selectionStore.item.reloadAnalysisForCurrentYear()
        } else if (typeof selectionStore.item.loadGeopolygonData === 'function') {
          selectionStore.item.analysis = new RegionAnalysis()
          await selectionStore.item.loadGeopolygonData()
        }
        break
      case selectionTypes.BOUNDARY:
        break
      case selectionTypes.ZIP_CODE_BOUNDARY:
        // if (typeof selectionStore.item.reloadAnalysisForCurrentYear === 'function' && !dsNameChanged) {
        //   selectionStore.item.reloadAnalysisForCurrentYear()
        // } else if (typeof selectionStore.item.loadAnalysis === 'function') {
        //   selectionStore.item.analysis = new RegionAnalysis()
        //   await selectionStore.item.loadAnalysis()
        // }
        break
      case selectionTypes.TARGET_GROUP:
        if (selectionStore.item) {
          selectionStore.item.analysis = new RegionAnalysis()
          await selectionStore.item.loadPyramids()
        }
        break
    }
    if (this.densityLayer !== 'none') {
      this.currentYear = dataStore.defaultYear.result
    }
  }

  addTourItemIfNeeded () {
    const markersCount = dataStore.targetGroups[0].savedMarkers.length
    if (!markersCount) {
      dataStore.targetGroups[0].savedMarkers.push(new SavedMarker({
        id: 'test',
        latitude: 0,
        longitude: 0,
        name: 'Example Marker'
      }))
    }
  }

  selectTourItem () {
    if (dataStore.targetGroups[0].savedMarkers[0].position._latitude) {
      selectionStore.set({
        item: dataStore.targetGroups[0].savedMarkers[0],
        type: selectionTypes.SAVED_MARKER,
        data: { group: dataStore.myTargetGroups[0] }
      })
    }
  }

  removeTourItem () {
    const markersCount = dataStore.targetGroups[0].savedMarkers.length
    if (markersCount === 1 &&
      dataStore.targetGroups[0].savedMarkers[0].id === 'test') {
      dataStore.targetGroups[0].savedMarkers.pop()
    }
  }

  @computed get hasExceededTargetGroupLimit () {
    if (this.user && this.user.isStarter) {
      return false
    }
    if (!this.user || !this.user.limits) {
      return false
    }
    return this.user.limits.max_target_groups <= dataStore.targetGroupCount
  }

  @computed get userTotalRegionsCount () {
    const res = _.sumBy(dataStore.myTargetGroups, target_group => {
      return target_group.regionsCount
    })
    return res
  }

  @computed get hasReachedStarterRegionLimit () {
    if (!this.user || !this.user.isStarter) return false
    if (this.user.region_limit_locked) return true
    const max = this.user.limits && this.user.limits.max_regions
    return !!max && this.starterShapesCreated >= max
  }

  @action registerStarterShapeCreated () {
    if (this.user && this.user.isStarter) {
      this.starterShapesCreated += 1
    }
  }

  @action applyFilters () {
    this.isFilterActive = true
  }

  @action clearFilters () {
    this.isFilterActive = false
    this.filters.resetFilters()
  }

  @computed get displayGroups () {
    return this.getFilteredGroups()
  }

  getFilteredGroups () {
    if (!this.filters) {
      return dataStore.targetGroups
    }
    const filteredGroups = dataStore.targetGroups.map(group => {
      const filteredGroup = Object.create(Object.getPrototypeOf(group))
      Object.assign(filteredGroup, group)
      const geopolygons = this.getFilteredGeopolygons(group)
      const boundaries = this.getFilteredBoundaries(group)
      const zipCodeBoundaries = this.getFilteredZipCodeBoundaries(group)
      const savedMarkers = this.getFilteredMarkers(group)

      Object.defineProperty(filteredGroup, 'geopolygons', { value: geopolygons, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'boundaries', { value: boundaries, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'zipCodeBoundaries', { value: zipCodeBoundaries, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'savedMarkers', { value: savedMarkers, configurable: true, enumerable: true })

      const allRegions = [...geopolygons, ...boundaries, ...zipCodeBoundaries]
      allRegions.sort((a, b) => new Date(a.created) - new Date(b.created))

      Object.defineProperty(filteredGroup, 'regions', { value: allRegions, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'regionsCount', { value: allRegions.length, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'markersCount', { value: savedMarkers.length, configurable: true, enumerable: true })
      Object.defineProperty(filteredGroup, 'itemsCount', { value: allRegions.length + savedMarkers.length, configurable: true, enumerable: true })

      return filteredGroup
    })

    if (!this.isFilterActive) {
      return filteredGroups
    }

    return filteredGroups.filter(group => {
      return group.geopolygons.length > 0 ||
        group.boundaries.length > 0 ||
        (group.zipCodeBoundaries && group.zipCodeBoundaries.length > 0) ||
        group.savedMarkers.length > 0
    })
  }

  getFilteredGeopolygons (group) {
    const type = this.filters.type.value || this.filters.type

    return group.geopolygons.filter(geo => {
      if (selectionStore.item === geo || selectionStore.editableShape === geo) {
        return true
      }
      if (type === FILTER_SETTINGS.MAP_ITEM_TYPES.BOUNDARY ||
        type === FILTER_SETTINGS.MAP_ITEM_TYPES.MARKER) {
        return false
      }
      if (this.filters.searchQuery &&
        !geo.name.toLowerCase().includes(this.filters.searchQuery.toLowerCase())) {
        return false
      }
      if (!this.filterByStatus(geo)) {
        return false
      }
      if (!this.filterByTags(geo)) {
        return false
      }
      if (!this.filterByAnalysis(geo.analysis, geo)) {
        return false
      }
      return true
    })
  }

  getFilteredBoundaries (group) {
    const type = this.filters.type.value || this.filters.type

    return group.boundaries.filter(boundary => {
      if (selectionStore.item === boundary || selectionStore.editableShape === boundary) {
        return true
      }
      if (type === FILTER_SETTINGS.MAP_ITEM_TYPES.GEOPOLYGON ||
        type === FILTER_SETTINGS.MAP_ITEM_TYPES.MARKER) {
        return false
      }
      if (this.filters.searchQuery &&
        !boundary.name.toLowerCase().includes(this.filters.searchQuery.toLowerCase())) {
        return false
      }
      if (!this.filterByStatus(boundary)) {
        return false
      }
      if (!this.filterByTags(boundary)) {
        return false
      }
      if (!this.filterByAnalysis(boundary.analysis, boundary)) {
        return false
      }
      return true
    })
  }

  getFilteredZipCodeBoundaries (group) {
    const type = this.filters.type.value || this.filters.type

    return (group.zipCodeBoundaries || []).filter(boundary => {
      if (selectionStore.item === boundary || selectionStore.editableShape === boundary) {
        return true
      }
      if (type === FILTER_SETTINGS.MAP_ITEM_TYPES.GEOPOLYGON ||
        type === FILTER_SETTINGS.MAP_ITEM_TYPES.MARKER) {
        return false
      }
      if (this.filters.searchQuery &&
        !boundary.name.toLowerCase().includes(this.filters.searchQuery.toLowerCase())) {
        return false
      }
      if (!this.filterByStatus(boundary)) {
        return false
      }
      if (!this.filterByTags(boundary)) {
        return false
      }
      if (!this.filterByAnalysis(boundary.analysis, boundary)) {
        return false
      }
      return true
    })
  }

  getFilteredMarkers (group) {
    const type = this.filters.type.value || this.filters.type
    if (type === FILTER_SETTINGS.MAP_ITEM_TYPES.GEOPOLYGON ||
      type === FILTER_SETTINGS.MAP_ITEM_TYPES.BOUNDARY) {
      return []
    }

    return group.savedMarkers.filter(marker => {
      if (selectionStore.item === marker) {
        return true
      }
      if (this.filters.searchQuery &&
        !marker.name.toLowerCase().includes(this.filters.searchQuery.toLowerCase())) {
        return false
      }
      if (!this.filterByStatus(marker)) {
        return false
      }
      if (!this.filterByTags(marker)) {
        return false
      }
      const incomeFilter = this.filters.income || this.filters.hhSize
      if (incomeFilter && !incomeFilter.isAtDefault && !incomeFilter.disabled) {
        let val = null
        if (marker.analysis && typeof marker.analysis.hh_size === 'number' && !isNaN(marker.analysis.hh_size)) {
          val = marker.analysis.hh_size
        } else if (typeof marker.hh_size === 'number' && !isNaN(marker.hh_size)) {
          val = marker.hh_size
        }
        const effectiveVal = (val != null && !isNaN(Number(val))) ? Number(val) : 0
        if (!this.filterByRange(effectiveVal, this.filters.incomeMin, this.filters.incomeMax)) {
          return false
        }
      }
      return true
    })
  }

  @action.bound
  scrollToSidebarItem (item, group) {
    if (!item) return { targetGroup: group, targetItem: item }

    if (this.sideMenuHidden) {
      this.sideMenuHidden = false
    }

    const isBoundaryOrZip = Boolean(
      item.remote_db_id != null ||
      item.inner_id != null ||
      item.isZipCode ||
      item.is_zip_code ||
      item.is_boundary ||
      item.isBoundary
    )

    let targetGroup = group
    let targetItem = item

    if (isBoundaryOrZip) {
      const itemName = (item.name || '').trim().toLowerCase()
      const itemRemoteId = item.remote_db_id != null ? String(item.remote_db_id) : null
      const itemInnerId = item.inner_id != null ? String(item.inner_id) : null
      const itemId = item.id != null ? String(item.id) : null

      let found = false

      // If a target group was explicitly provided (e.g. on creation), look in that group first
      if (group) {
        const candidates = group.regions || [
          ...(group.boundaries || []),
          ...(group.zipCodeBoundaries || []),
          ...(group.geopolygons || [])
        ]

        for (const candidate of candidates) {
          const candName = (candidate.name || '').trim().toLowerCase()
          const candRemoteId = candidate.remote_db_id != null ? String(candidate.remote_db_id) : null
          const candInnerId = candidate.inner_id != null ? String(candidate.inner_id) : null
          const candId = candidate.id != null ? String(candidate.id) : null

          const nameMatches = Boolean(itemName && candName && (candName === itemName))
          const idMatches = Boolean(
            (itemRemoteId && candRemoteId && itemRemoteId === candRemoteId) ||
            (itemInnerId && candInnerId && itemInnerId === candInnerId) ||
            (itemId && candId && itemId === candId)
          )

          if (nameMatches || idMatches) {
            targetGroup = group
            targetItem = candidate
            found = true
            break
          }
        }
      }

      // If no group was specified, or the item was not in the specified group, search across all groups
      if (!found) {
        const allDisplayGroups = [
          ...(this.myDisplayGroups || []),
          ...(dataStore.sharedTargetGroups || [])
        ]

        for (const tg of allDisplayGroups) {
          const candidates = tg.regions || [
            ...(tg.boundaries || []),
            ...(tg.zipCodeBoundaries || []),
            ...(tg.geopolygons || [])
          ]

          for (const candidate of candidates) {
            const candName = (candidate.name || '').trim().toLowerCase()
            const candRemoteId = candidate.remote_db_id != null ? String(candidate.remote_db_id) : null
            const candInnerId = candidate.inner_id != null ? String(candidate.inner_id) : null
            const candId = candidate.id != null ? String(candidate.id) : null

            const nameMatches = Boolean(itemName && candName && (candName === itemName))
            const idMatches = Boolean(
              (itemRemoteId && candRemoteId && itemRemoteId === candRemoteId) ||
              (itemInnerId && candInnerId && itemInnerId === candInnerId) ||
              (itemId && candId && itemId === candId)
            )

            if (nameMatches || idMatches) {
              targetGroup = tg
              targetItem = candidate
              found = true
              break
            }
          }
          if (found) break
        }
      }
    }

    const isTargetGroup = Boolean(
      (item && item.pk != null && (item.regions !== undefined || item.is_owner !== undefined || item === targetGroup)) ||
      (targetItem && targetItem.pk != null && (targetItem.regions !== undefined || targetItem.is_owner !== undefined || targetItem === targetGroup))
    )

    if (targetGroup) {
      targetGroup.expanded = true
      this.expandTargetGroup(targetGroup.pk)
      if (!targetGroup.is_owner) {
        this.sharedTargetGroupsExpanded = true
      }
    }

    const performScroll = (allowFallbackToGroup = false) => {
      const scrollContainer = document.querySelector('.map-data') || document.querySelector('.tools') || document.body
      if (!scrollContainer) return false

      const groupPk = targetGroup ? String(targetGroup.pk) : null
      const targetName = targetItem && targetItem.name ? String(targetItem.name).trim().toLowerCase() : null
      const innerId = targetItem && targetItem.inner_id != null ? String(targetItem.inner_id) : null
      const remoteDbId = targetItem && targetItem.remote_db_id != null ? String(targetItem.remote_db_id) : null
      const itemId = targetItem && targetItem.id != null ? String(targetItem.id) : null

      if (isTargetGroup && groupPk) {
        const groupEl = document.querySelector(`.target-group[data-target-group-id="${groupPk}"], .target-group[data-group-pk="${groupPk}"], .target-group button[data-group-id="${groupPk}"]`)
        if (groupEl) {
          const scrollTarget = groupEl.closest ? (groupEl.closest('.target-group') || groupEl) : groupEl
          try {
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
          } catch (e) {
            scrollTarget.scrollIntoView()
          }
          return true
        }
      }

      const searchInScope = (scope) => {
        if (!scope) return null
        const items = scope.querySelectorAll('.item-list__item')
        for (const el of items) {
          const elInnerId = el.getAttribute('data-inner-id')
          const elRemoteDbId = el.getAttribute('data-remote-db-id')
          const elItemId = el.getAttribute('data-item-id')
          const elMarkerId = el.getAttribute('data-marker-id')
          const elName = (el.getAttribute('data-item-name') || '').trim().toLowerCase()

          if (innerId && elInnerId && elInnerId === innerId) return el
          if (remoteDbId && elRemoteDbId && elRemoteDbId === remoteDbId) return el
          if (itemId && ((elItemId && elItemId === itemId) || (elMarkerId && elMarkerId === itemId))) return el
          if (targetName && elName && elName === targetName) return el
          if (el.classList.contains('selected')) return el
        }
        return null
      }

      let element = null

      if (groupPk) {
        const groupEl = document.querySelector(`.target-group[data-target-group-id="${groupPk}"], .target-group[data-group-pk="${groupPk}"], .target-group button[data-group-id="${groupPk}"]`)
        if (groupEl) {
          const parentGroupEl = groupEl.closest ? (groupEl.closest('.target-group') || groupEl) : groupEl
          element = searchInScope(parentGroupEl)
        }
      }

      if (!element) {
        element = searchInScope(scrollContainer)
      }

      if (!element && (allowFallbackToGroup || isTargetGroup) && groupPk) {
        element = document.querySelector(`.target-group[data-target-group-id="${groupPk}"], .target-group[data-group-pk="${groupPk}"], .target-group button[data-group-id="${groupPk}"]`)
      }

      if (element) {
        try {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        } catch (e) {
          element.scrollIntoView()
        }
        return true
      }
      return false
    }

    const scheduleScrollAttempts = () => {
      if (performScroll(false)) return

      const delays = [30, 80, 150, 300, 500]
      delays.forEach((delay, idx) => {
        setTimeout(() => {
          const isLast = idx === delays.length - 1
          performScroll(isLast)
        }, delay)
      })
    }

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(scheduleScrollAttempts)
    } else {
      scheduleScrollAttempts()
    }

    return { targetGroup, targetItem }
  }

  @action.bound
  expandTargetGroup (groupPk) {
    if (groupPk == null) return
    const pkStr = String(groupPk)

    const matchAndExpand = (g) => {
      if (g && String(g.pk) === pkStr) {
        g.expanded = true
        if (!g.is_owner) {
          this.sharedTargetGroupsExpanded = true
        }
      }
    }

    ;(dataStore.targetGroups || []).forEach(matchAndExpand)
    ;(dataStore.sharedTargetGroups || []).forEach(matchAndExpand)
    ;(this.displayGroups || []).forEach(matchAndExpand)
  }

  toggleTargetGroupExpanded (groupPk) {
    const group = dataStore.targetGroups.find(g => g.pk === groupPk)
    if (group) {
      group.expanded = !group.expanded
      const sourceGroup = dataStore.targetGroups.find(g => g.pk === groupPk)
      if (sourceGroup) {
        sourceGroup.expanded = group.expanded
      }
    }
  }

  // Mutating `group.visible` directly on the object passed to TargetGroup.jsx
  // doesn't work: that prop comes from displayGroups/getFilteredGroups(),
  // which builds a fresh, throwaway clone of each real TargetGroup via
  // Object.assign - the clone's `visible` is a plain snapshotted value, not
  // the real observable, so writes to it never reach dataStore.targetGroups
  // and get silently discarded on the next recompute. Look the real instance
  // up by pk instead, matching toggleTargetGroupExpanded.
  toggleTargetGroupVisible (groupPk) {
    const group = dataStore.targetGroups.find(g => g.pk === groupPk)
    if (!group) return
    if (!group.visible && !group.loading && group.regions.length === 0) {
      group.loadGeometries(null, null, selectionStore.cancelSource.token)
      group.loadBoundaries(selectionStore.cancelSource.token)
      group.loadZipCodeBoundaries(selectionStore.cancelSource.token)
      group.loadSavedMarkers(selectionStore.cancelSource.token)
    }
    group.visible = !group.visible
  }

  toggleMarkerLabels () {
    this.showMarkerLabels = !this.showMarkerLabels
  }

  toggleZoomToSelected () {
    this.zoomToSelected = !this.zoomToSelected
  }

  showBarChart () {
    this.showIncomeTooltip = false
    this.isBarChartActive = !this.isBarChartActive
  }

  toggleHeatmapSettings () {
    if (this.isHeatmapSettingsVisible) {
      this.isHeatmapSettingsVisible = false
    } else {
      this.isHeatmapSettingsVisible = true
      this.hideFilterPopup()
      this.hideSortPopup()
      this.hideLayerSettings()
      if (selectionStore) selectionStore.hidePopupMenu()
      this.hideClickTooltip()
      this.hideDeletePrompt()
    }
  }

  hideHeatmapSettings () {
    this.isHeatmapSettingsVisible = false
  }

  toggleLayerSettings () {
    if (this.isLayerSettingsVisible) {
      this.isLayerSettingsVisible = false
      this.hideMapSettingsMenu()
    } else {
      this.isLayerSettingsVisible = true
      this.hideFilterPopup()
      this.hideSortPopup()
      this.hideHeatmapSettings()
      if (selectionStore) selectionStore.hidePopupMenu()
      this.hideClickTooltip()
      this.hideDeletePrompt()
    }
  }

  togglePOIMenu () {
    const wasOpen = this.isPOIMenuExpanded
    this.isPOIMenuExpanded = !this.isPOIMenuExpanded
    // If menu is opening, only populate results based on current selection
    // if there are no active search results(preserve search results when reopening)
    if (!wasOpen && this.isPOIMenuExpanded) {
      const poi = dataStore && dataStore.poi
      const hasActiveSearchResults = poi && (
        (poi.placeResults && poi.placeResults.length > 0) ||
        (poi.temporaryMarkers && poi.temporaryMarkers.length > 0 &&
          // Check if temporaryMarkers contain search results(not just saved markers)
          poi.temporaryMarkers.some(m => !m.savedMarkerId && !String(m.id).startsWith('saved_'))) ||
        (this.poiSearchQuery && this.poiSearchQuery.trim().length > 0)
      )
      // Only repopulate if there are no active search results to preserve
      if (!hasActiveSearchResults) {
        this.populatePoiResultsForCurrentSelection()
      }
    }
  }

  /**
   * Populate POI results based on current selection when menu opens
   */
  @action.bound
  async populatePoiResultsForCurrentSelection () {
    if (!dataStore || !dataStore.poi || !selectionStore) return

    const selectionType = selectionStore.type
    const selectionItem = selectionStore.item || selectionStore._item

    // If a marker is selected, show only that marker
    if (selectionType === selectionTypes.SAVED_MARKER && selectionItem) {
      const marker = selectionItem
      const targetGroup = selectionStore.data && selectionStore.data.group

      if (marker && targetGroup) {
        await targetGroup.loadSavedMarkers()

        // Build temporary marker format for this specific marker
        if (marker.position && marker.position.latitude != null && marker.position.longitude != null) {
          const tempMarker = {
            id: `saved_${marker.id}`,
            savedMarkerId: marker.id,
            position: { latLng: { lat: marker.position.latitude, lng: marker.position.longitude } },
            name: marker.name || '',
            address: marker.description || marker.address || '',
            type: (marker.types && typeof marker.types === 'string') ? marker.types : '',
            types: marker.types || '',
            getColor: marker.color_id || 1,
            icon_url: marker.icon_url || null,
            is_general: marker.is_general,
            is_ally: marker.is_ally,
            is_competitor: marker.is_competitor
          }

          // Set this marker as the only temporary marker
          dataStore.poi.temporaryMarkers = [tempMarker]
          dataStore.poi.placeResults = []
          dataStore.poi.currentTargetGroupId = targetGroup.pk

          // Sync buckets so View section shows bucket cards if marker belongs to any group
          dataStore.poi.syncBucketsFromSavedMarkers(targetGroup)
        }
      }
    } else if (selectionType === selectionTypes.TARGET_GROUP && selectionItem) {
      // If target group is selected, show all markers from that group
      await this.openPoiForTargetGroup(selectionItem)
    } else if (selectionItem && selectionItem.geoJSON) {
      // If a shape is selected, show markers inside that shape
      const targetGroup = selectionStore.data && selectionStore.data.group
      if (targetGroup) {
        await targetGroup.loadSavedMarkers()

        const shapeGeoJSON = selectionItem.geoJSON
        const tempMarkers = (targetGroup.savedMarkers || [])
          .filter(sm => {
            if (!sm.position || sm.position.latitude == null || sm.position.longitude == null) return false
            return dataStore.poi.pointInGeoJSON(
              sm.position.latitude,
              sm.position.longitude,
              shapeGeoJSON
            )
          })
          .map(sm => ({
            id: `saved_${sm.id}`,
            savedMarkerId: sm.id,
            position: { latLng: { lat: sm.position.latitude, lng: sm.position.longitude } },
            name: sm.name || '',
            address: sm.description || sm.address || '',
            type: (sm.types && typeof sm.types === 'string') ? sm.types : '',
            types: sm.types || '',
            getColor: sm.color_id || 1,
            icon_url: sm.icon_url || undefined,
            is_general: sm.is_general,
            is_ally: sm.is_ally,
            is_competitor: sm.is_competitor
          }))

        dataStore.poi.temporaryMarkers = tempMarkers
        dataStore.poi.placeResults = []
        dataStore.poi.currentTargetGroupId = targetGroup.pk
        dataStore.poi.syncBucketsFromSavedMarkers(targetGroup)
      }
    }
  }

  hideLayerSettings () {
    this.isLayerSettingsVisible = false
    this.hideMapSettingsMenu()
  }

  toggleMapSettingsMenu () {
    if (this.isMapSettingsVisible) {
      this.isMapSettingsVisible = false
    } else {
      this.isMapSettingsVisible = true
    }
  }

  hideMapSettingsMenu () {
    this.isMapSettingsVisible = false
  }

  toggleDateYearModal () {
    if (this.user && this.user.isStarter) return
    this.isDateYearModalVisible = !this.isDateYearModalVisible
  }

  showDateYearModal () {
    if (this.user && this.user.isStarter) return
    this.isDateYearModalVisible = true
  }

  hideDateYearModal () {
    this.isDateYearModalVisible = false
  }

  setQuickSettingLayer () {
    this.baseMap = this.quickSettingLayer
  }

  @action snapshotSelectionBeforeAdd () {
    if (this.previousSelectionBeforeAdd) return
    if (selectionStore && (selectionStore.item || this.isPOIMenuExpanded)) {
      this.previousSelectionBeforeAdd = {
        item: selectionStore.item,
        data: selectionStore.data ? { ...selectionStore.data } : null,
        activeTab: this.activeTab,
        wasPOIMenuExpanded: this.isPOIMenuExpanded
      }
    }
  }

  @action restorePreviousSelectionBeforeAdd () {
    const prev = this.previousSelectionBeforeAdd
    this.previousSelectionBeforeAdd = null
    if (!prev) return false

    if (prev.item) {
      if (typeof selectionStore.clearSelectedMenuItems === 'function') {
        selectionStore.clearSelectedMenuItems()
      }
      if (typeof selectionStore.addSelectedMenuItem === 'function') {
        selectionStore.addSelectedMenuItem(prev.item)
      }
      selectionStore.select(prev.item, prev.data ? { data: prev.data } : undefined)

      const shapeId = prev.item.id || prev.item.inner_id || prev.item.remote_db_id
      if (rightSidebarStore && shapeId && prev.item.layer !== 'marker' && !prev.item.is_general) {
        if (!rightSidebarStore.singlePolygonData || String(rightSidebarStore.singlePolygonData.id) !== String(shapeId)) {
          rightSidebarStore.fetchDataForSinglePolygon(shapeId, prev.item.name, getShapePopulationFetchOptions(prev.item))
        }
      } else if (prev.item.pk && !prev.item.layer && rightSidebarStore) {
        rightSidebarStore.fetchPopulationDataForTargetGroup(prev.item.pk, prev.item.name)
      }
    } else {
      selectionStore.select(null, { type: selectionTypes.NONE })
    }

    selectionStore.hideSubTools()

    if (prev.wasPOIMenuExpanded || prev.item) {
      this.isPOIMenuExpanded = true
      if (prev.activeTab) {
        this.setActiveTab(prev.activeTab)
      }
    }
    return true
  }

  @action clearPreviousSelectionBeforeAdd () {
    this.previousSelectionBeforeAdd = null
  }

  showChooseAddType () {
    this.clearHoveredTargetGroup()
    this.snapshotSelectionBeforeAdd()
    this.isPOIMenuExpanded = false
    this.isAddChoiceTypeVisible = true
  }

  hideChooseAddType () {
    this.isAddChoiceTypeVisible = false
  }

  hideColorsMenu () {
    this.isColorPickerExpanded = false
  }

  showColorsMenu () {
    this.isColorPickerExpanded = true
  }

  @computed get toolsWidth () {
    if (this.sideMenuHidden) {
      return 0
    }

    let width = selectionStore.showSubTools
      ? (this.menuWidth ? this.menuWidth * 2 : constants.TOOLS_WIDTH * 2)
      : (this.menuWidth || constants.TOOLS_WIDTH)

    if (this.showClickTooltip || selectionStore.isEditing || this.showDeletePrompt) {
      width = Math.max(width, 680)
    }

    return width
  }

  @computed get analysisHeight () {
    if (_.isNil(selectionStore.item) || _.isNil(selectionStore.item.analysis) || selectionStore.isEditing) {
      return 5
    } else if (selectionStore.item.analysis instanceof RegionAnalysis) {
      return 331
    } else {
      return 61
    }
  }

  mapSize () {
    const element = typeof document !== 'undefined'
      ? document.querySelector('.inner>.map')
      : null
    if (element && element.offsetWidth > 0 && element.offsetHeight > 0) {
      return {
        width: element.offsetWidth,
        height: element.offsetHeight
      }
    }
    // During initial load the map is not mounted yet (`loading` replaces `.inner`).
    // Use the window size so fit-padding matches the first click after the map appears.
    // A 100x100 fallback made the first group click recompute much larger padding and zoom out.
    if (typeof window !== 'undefined' && window.innerWidth > 0 && window.innerHeight > 0) {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }
    return {
      width: 100,
      height: 100
    }
  }

  async onAuthenticationComplete () {
    this.authenticated = true
    await dataStore.loadTargetGroups()
    dataStore.setFilterMaxAndMins(this)
    let itemToFitAfterMapReady = null
    if (dataStore.targetGroups.length > 0) {
      const firstGroup = dataStore.myTargetGroups[0] || dataStore.targetGroups[0]

      // Initial automatic selection should NOT
      // open the POI menu. Mark that the next selection change should
      // skip POI menu logic once.
      this.skipNextPOIMenuOpen = true

      // Zoom to and select the first element of the target group having children.
      // Zip code should not get priority over shapes or admin boundaries.
      const allGroups = [...(dataStore.myTargetGroups || []), ...(dataStore.targetGroups || [])]
      const uniqueGroups = _.uniqBy(allGroups, 'pk')

      const firstShapeGroup = uniqueGroups.find(g =>
        (g && g.regions && g.regions.length > 0) ||
        (g && g.savedMarkers && g.savedMarkers.length > 0) ||
        (g && g.itemsCount && g.itemsCount > 0)
      ) || firstGroup

      let firstShape = null
      if (firstShapeGroup) {
        if (firstShapeGroup.regions && firstShapeGroup.regions.length > 0) {
          // If the group has a shape (geopolygon) or admin boundary, select that first.
          // Zip code only gets selected if there are no other shapes or admin boundaries.
          firstShape = firstShapeGroup.regions.find(r => !r.isZipCode && !r.is_zip_code) || firstShapeGroup.regions[0]
        } else if (firstShapeGroup.geopolygons && firstShapeGroup.geopolygons.length > 0) {
          firstShape = firstShapeGroup.geopolygons[0]
        } else if (firstShapeGroup.boundaries && firstShapeGroup.boundaries.length > 0) {
          firstShape = firstShapeGroup.boundaries[0]
        } else if (firstShapeGroup.zipCodeBoundaries && firstShapeGroup.zipCodeBoundaries.length > 0) {
          firstShape = firstShapeGroup.zipCodeBoundaries[0]
        } else if (firstShapeGroup.savedMarkers && firstShapeGroup.savedMarkers.length > 0) {
          firstShape = firstShapeGroup.savedMarkers[0]
        }
      }

      const activeGroup = firstShapeGroup || firstGroup
      const firstShapeId = firstShape && (firstShape.remote_db_id != null ? firstShape.remote_db_id : (firstShape.id || firstShape.inner_id))

      if (firstShape && typeof firstShape.bounds === 'function') {
        if (activeGroup) {
          activeGroup.expanded = true
        }
        selectionStore.clearSelectedMenuItems()
        selectionStore.addSelectedMenuItem(firstShape)
        selectionStore.select(firstShape, { data: { group: activeGroup } })
        try {
          const b = firstShape.bounds()
          if (b) {
            this.setBoundsToItem(firstShape)
            itemToFitAfterMapReady = firstShape
          } else if (activeGroup && typeof activeGroup.bounds === 'function') {
            const gb = activeGroup.bounds()
            if (gb && typeof gb.isValid === 'function' && gb.isValid()) {
              this.setBoundsToItem(activeGroup)
              itemToFitAfterMapReady = activeGroup
            }
          }
        } catch (e) {
          console.warn('Could not set initial bounds to firstShape', e)
        }
        if (firstShapeId && (firstShape.polygon || firstShape.geometry || firstShape.remote_db_id != null)) {
          try {
            await rightSidebarStore.fetchDataForSinglePolygon(
              firstShapeId,
              firstShape.name,
              getShapePopulationFetchOptions(firstShape)
            )
          } catch (e) {
            console.warn('Failed to fetch demographic data for firstShape', e)
          }
        }
      } else {
        // Fallback: select the first target group if no shape exists.
        if (activeGroup) {
          activeGroup.expanded = true
        }
        selectionStore.clearSelectedMenuItems()
        selectionStore.addSelectedMenuItemGroup(activeGroup)
        selectionStore.select(activeGroup)
        itemToFitAfterMapReady = activeGroup
        if (activeGroup && activeGroup.pk) {
          await rightSidebarStore.fetchPopulationDataForTargetGroup(activeGroup.pk, activeGroup.name)
        }
      }
    }
    this.loading = false
    // Re-fit once the map is in the DOM so padding uses the real map size,
    // matching later target-group clicks.
    this.scheduleFitBoundsWhenMapReady(itemToFitAfterMapReady)
  }

  scheduleFitBoundsWhenMapReady (item) {
    if (!item || typeof item.bounds !== 'function') return
    const tryFit = (attempts = 0) => {
      if (selectionStore.item !== item) return
      const mapEl = typeof document !== 'undefined' && document.querySelector('.inner>.map')
      if (mapEl && mapEl.offsetWidth > 0 && mapEl.offsetHeight > 0) {
        this.setBoundsToItem(item)
        return
      }
      if (attempts < 40) {
        requestAnimationFrame(() => tryFit(attempts + 1))
      }
    }
    requestAnimationFrame(() => tryFit())
  }

  checkForInitialHelpTour () {
  }

  checkForDataUpdateInfoModal () {
    if (this.user && this.user.needs_recalculation) {
      this.dataUpdateInfoModal = true
    }
  }

  setBoundsToItem (item) {
    if (item.shapefile) {
      const activeLevel = item.shapefile.selectedLevel
      activeLevel.preview = true
    }

    // Recognize the marker itself: analysis/POI lists can focus a saved marker
    // while its parent shape remains selected.
    try {
      if (
        selectionStore &&
        (item instanceof SavedMarker || selectionStore.type === selectionTypes.SAVED_MARKER) &&
        this.map &&
        this.map.leafletElement &&
        item &&
        item.position &&
        item.position.latitude != null &&
        item.position.longitude != null &&
        typeof item.bounds === 'function'
      ) {
        if (this.zoomToMarker([item.position.latitude, item.position.longitude], item.bounds())) {
          if (
            selectionStore.type !== selectionTypes.BOUNDARY &&
            selectionStore.type !== selectionTypes.BOUNDARY_LIST &&
            selectionStore.type !== selectionTypes.USER_MARKERFILE_LIST &&
            selectionStore.type !== selectionTypes.USER_SHAPEFILE_LIST
          ) {
            selectionStore.hideSubTools()
          }
          return
        }
      }
    } catch (e) {
      // Fallback to bounds behavior if anything above fails
    }

    const bounds = item.bounds()
    this.setBounds(bounds)
    if (selectionStore.type !== selectionTypes.BOUNDARY && selectionStore.type !== selectionTypes.BOUNDARY_LIST && selectionStore.type !== selectionTypes.USER_MARKERFILE_LIST && selectionStore.type !== selectionTypes.USER_SHAPEFILE_LIST) {
      selectionStore.hideSubTools()
    }
  }

  zoomToMarker (position, bufferedBounds) {
    const map = this.map && this.map.leafletElement
    if (!map || !position || typeof map.getZoom !== 'function') return false
    const center = Array.isArray(position) ? position : [position.lat, position.lng]
    const currentZoom = map.getZoom()
    if (!Number.isFinite(currentZoom) || !center.every(Number.isFinite)) return false

    // A newly selected marker must move closer when zoom is available. Checking
    // visibility or a buffer threshold left some clicks on a center-only path.
    // Keep the buffer as a minimum useful zoom when starting from a world view.
    const buffer = 0.008
    const bounds = new LatLngBounds(bufferedBounds || [
      [center[0] - buffer, center[1] - buffer],
      [center[0] + buffer, center[1] + buffer]
    ])
    const bufferZoom = typeof map.getBoundsZoom === 'function' ? map.getBoundsZoom(bounds, false) : null
    const requestedZoom = Number.isFinite(bufferZoom) ? Math.max(currentZoom + 1, bufferZoom) : currentZoom + 1
    const maxZoom = typeof map.getMaxZoom === 'function' ? map.getMaxZoom() : Infinity
    const targetZoom = Math.max(currentZoom, Math.min(requestedZoom, maxZoom))

    this.updateBoundsOptionsHelper()
    const size = map.getSize()
    const leftOffset = Math.min(size.x - 1, this.toolsWidth)
    const rightOffset = Math.min(size.x - leftOffset - 1, this.isPOIMenuExpanded ? 480 : 80)
    const visibleCenterX = leftOffset + (size.x - leftOffset - rightOffset) / 2
    const screenCenterX = size.x / 2
    const pixelShiftX = screenCenterX - visibleCenterX

    const centerPoint = map.project(center, targetZoom)
    const offsetPoint = centerPoint.add([pixelShiftX, 0])
    const targetCenter = map.unproject(offsetPoint, targetZoom)

    runInAction(() => {
      this.bounds = undefined
      this.viewport = { center: [targetCenter.lat, targetCenter.lng], zoom: targetZoom }
    })

    if (map && typeof map.setView === 'function') {
      try {
        map.setView(targetCenter, targetZoom)
      } catch (e) {
        // ignore
      }
    }

    return true
  }

  setBounds (bounds) {
    this.setBoundsHelper(bounds)
  }

  @action setBoundsHelper (bounds) {
    this.updateBoundsOptionsHelper()
    if (!bounds) return
    const boundsObject = new LatLngBounds().extend(bounds)
    if (!boundsObject.isValid()) return

    const sw = boundsObject.getSouthWest()
    const ne = boundsObject.getNorthEast()
    if (
      !Number.isFinite(sw.lat) || !Number.isFinite(sw.lng) ||
      !Number.isFinite(ne.lat) || !Number.isFinite(ne.lng)
    ) {
      return
    }

    let finalBounds = boundsObject
    if (Math.abs(sw.lat - ne.lat) < 0.0001 && Math.abs(sw.lng - ne.lng) < 0.0001) {
      const buffer = 0.008
      finalBounds = new LatLngBounds(
        [sw.lat - buffer, sw.lng - buffer],
        [ne.lat + buffer, ne.lng + buffer]
      )
    }

    this.bounds = finalBounds
  }

  @action resetBounds () {
    this.bounds = new LatLngBounds([-65, -65], [65, 65])
  }

  @action setFoundLocation (location) {
    this.foundLocation = location
  }

  @computed get location () {
    return toJS(this.foundLocation)
  }

  updateBoundsOptions () {
    _.defer(() => this.updateBoundsOptionsHelper())
  }

  @action updateBoundsOptionsHelper () {
    const mapSize = this.mapSize()
    const leftOffset = this.print
      ? 0
      : Math.min(mapSize.width - 1, this.toolsWidth)
    const rightOffset = this.print
      ? 0
      : Math.min(mapSize.width - leftOffset - 1, this.isPOIMenuExpanded ? 480 : 80)
    const bottomOffset = this.print
      ? 0
      : Math.min(mapSize.height - 1, this.analysisHeight)
    const width = mapSize.width - leftOffset - rightOffset
    const height = mapSize.height - bottomOffset

    const xPadding = width > (constants.BOUNDS_PADDING * 4)
      ? constants.BOUNDS_PADDING
      : 0

    const yPadding = height > (constants.BOUNDS_PADDING * 4)
      ? constants.BOUNDS_PADDING
      : 0

    const left = new Point(xPadding + leftOffset, yPadding)
    const right = new Point(xPadding + rightOffset, yPadding + bottomOffset)

    if (this.print) {
      this.printLeftOffset = left
      this.printRightOffset = right
    } else {
      this.boundsTopLeftOffset = left
      this.boundsBottomRightOffset = right
    }
  }

  printScreen () {
    this.print = true
    const isGoogle = this.baseMap.indexOf('google') >= 0
    const delay = isGoogle ? 800 : 350
    this.updateBoundsOptionsHelper()
    setTimeout(() => {
      window.print()
      this.print = false
    }, delay)
  }

  showAddTargetGroupPane () {
    if (!this.hasExceededTargetGroupLimit) {
      const map = this.map && this.map.leafletElement
      const currentBounds = map && typeof map.getBounds === 'function' ? map.getBounds() : null
      if (typeof this.suppressMapBoundsTemporarily === 'function') {
        this.suppressMapBoundsTemporarily(1000)
      }
      if (currentBounds && typeof this.setBoundsHelper === 'function') {
        this.setBoundsHelper(currentBounds)
      }
      dataStore.initializeAddTargetGroupForm()
      selectionStore.select(null, { type: selectionTypes.NEW_TARGET_GROUP })
    }
  }

  async addTargetGroup (group) {
    this.clearPreviousSelectionBeforeAdd()
    if (_.find(dataStore.myTargetGroups, { name: group.name })) {
      group.error = i18next.t('GLOBAL.DUPLICATE_GROUP_NAME')
      return
    }

    this.processing = true
    const pk = await dataStore.addTargetGroup(group)
    const createdGroup = _.find(dataStore.targetGroups, { pk })
    selectionStore.select(createdGroup)
    selectionStore.hideSubTools()
    if (createdGroup && rightSidebarStore) {
      await rightSidebarStore.fetchPopulationDataForTargetGroup(createdGroup.pk, createdGroup.name)
    }
    this.processing = false
  }

  async renameTargetGroup (group, name) {
    if (group.name === name) {
      return
    }

    if (_.find(dataStore.myTargetGroups, { name })) {
      group.error = i18next.t('GLOBAL.DUPLICATE_GROUP_NAME')
      return
    }

    if (name === '') {
      group.error = i18next.t('GLOBAL.EMPTY_SHAPE_NAME')
      return
    }

    this.processing = true
    await dataStore.renameTargetGroup(group, name)
    this.processing = false
  }

  async deleteTargetGroup (group) {
    this.processing = true
    await dataStore.deleteTargetGroup(group)
    selectionStore.clear()
    this.processing = false
  }

  clear () {
    this.processing = false
    selectionStore.clear()
  }

  async deleteGeopolygon (group, geopolygon) {
    this.processing = true
    const realGroup = (dataStore.targetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      (dataStore.sharedTargetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      group

    const geoId = geopolygon && (geopolygon.id || geopolygon)
    if (realGroup && realGroup.geopolygons) {
      realGroup.geopolygons = (realGroup.geopolygons || []).filter(g => g !== geopolygon && g.id !== geoId)
    }
    if (group && group !== realGroup && group.geopolygons) {
      group.geopolygons = (group.geopolygons || []).filter(g => g !== geopolygon && g.id !== geoId)
    }

    const isSelected = selectionStore.item && (
      selectionStore.item === geopolygon ||
      selectionStore.item.id === geoId ||
      selectionStore.item.id === (geopolygon && geopolygon.id)
    )
    if (isSelected) {
      selectionStore.clear()
    } else {
      selectionStore.selectedMenuItems = (selectionStore.selectedMenuItems || []).filter(m => m !== geopolygon && m.id !== geoId)
      selectionStore.hidePopupMenu()
    }

    await realGroup.deleteGeopolygon(geopolygon)
    this.processing = false
  }

  async deleteBoundary (group, boundary) {
    this.processing = true
    const realGroup = (dataStore.targetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      (dataStore.sharedTargetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      group

    const innerId = boundary && (boundary.inner_id !== undefined ? boundary.inner_id : (boundary.id || boundary))
    const remoteId = boundary && (boundary.remote_db_id !== undefined ? boundary.remote_db_id : (boundary.id || boundary))

    if (realGroup) {
      if (realGroup.boundaries) {
        realGroup.boundaries = (realGroup.boundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      }
      if (realGroup.zipCodeBoundaries) {
        realGroup.zipCodeBoundaries = (realGroup.zipCodeBoundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      }
    }
    if (group && group !== realGroup) {
      if (group.boundaries) {
        group.boundaries = (group.boundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      }
      if (group.zipCodeBoundaries) {
        group.zipCodeBoundaries = (group.zipCodeBoundaries || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      }
    }

    const isSelected = selectionStore.item && (
      selectionStore.item === boundary ||
      (innerId != null && (selectionStore.item.inner_id === innerId || selectionStore.item.id === innerId)) ||
      (remoteId != null && (selectionStore.item.remote_db_id === remoteId || selectionStore.item.id === remoteId))
    )
    if (isSelected) {
      selectionStore.clear()
    } else {
      selectionStore.selectedMenuItems = (selectionStore.selectedMenuItems || []).filter(b => b !== boundary && b.inner_id !== innerId && b.remote_db_id !== remoteId && b.id !== innerId)
      selectionStore.hidePopupMenu()
    }

    await realGroup.deleteBoundary(boundary)
    this.processing = false
  }

  async deleteSavedMarker (group, marker) {
    this.processing = true
    const realGroup = (dataStore.targetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      (dataStore.sharedTargetGroups || []).find(g => g && g.pk === (group && group.pk)) ||
      group

    const markerId = marker && (marker.id || marker)
    if (realGroup && realGroup.savedMarkers) {
      realGroup.savedMarkers = (realGroup.savedMarkers || []).filter(m => m !== marker && m.id !== markerId)
    }
    if (group && group !== realGroup && group.savedMarkers) {
      group.savedMarkers = (group.savedMarkers || []).filter(m => m !== marker && m.id !== markerId)
    }

    const isSelected = selectionStore.item && (
      selectionStore.item === marker ||
      selectionStore.item.id === markerId
    )
    if (isSelected) {
      selectionStore.clear()
    } else {
      selectionStore.selectedMenuItems = (selectionStore.selectedMenuItems || []).filter(m => m !== marker && m.id !== markerId)
      selectionStore.hidePopupMenu()
    }

    await realGroup.deleteSavedMarker(marker)

    // Also remove from POI bucket state and temporaryMarkers if it exists there
    // This ensures marker disappears from POI menu search results when deleted from tools menu
    if (dataStore.poi) {
      dataStore.poi.removeMarkerFromAllBucketsBySavedMarker(marker, true)
    }

    this.processing = false
  }

  resolveTargetGroup (group) {
    if (!group || !group.pk || !dataStore) return group
    return (dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(group.pk))) ||
      (dataStore.sharedTargetGroups && dataStore.sharedTargetGroups.find(g => String(g.pk) === String(group.pk))) ||
      group
  }

  async addGeopolygon (group, shape) {
    this.clearPreviousSelectionBeforeAdd()
    const realGroup = this.resolveTargetGroup(group)
    this.isPOIMenuExpanded = true
    this.setActiveTab('people')
    rightSidebarStore.resetData()
    rightSidebarStore.targetGroupName = shape.name || 'New Shape'
    rightSidebarStore.loading = true

    this.processing = true
    const geopolygon = await shape.add(realGroup.pk)
    if (geopolygon) {
      this.registerStarterShapeCreated()
      await realGroup.loadGeometries(geopolygon.id, geopolygon.task_id)
      await realGroup.loadBoundaries()
      if (group && group !== realGroup) {
        await group.loadGeometries(geopolygon.id, geopolygon.task_id)
        await group.loadBoundaries()
        group.expanded = true
      }
      realGroup.expanded = true
      dataStore.setFilterMaxAndMins(this)
      this.isFilterActive = false
      this.filters.resetFilters()
      const newGeopolygon = _.find(realGroup.geopolygons, { id: geopolygon.id }) || _.find(group.geopolygons, { id: geopolygon.id })
      if (newGeopolygon) {
        selectionStore.clearSelectedMenuItems()
        selectionStore.addSelectedMenuItem(newGeopolygon)
        selectionStore.select(newGeopolygon, { data: { group: realGroup } })
        this.setBoundsToItem(newGeopolygon)
        selectionStore.hideSubTools()
        rightSidebarStore.fetchDataForSinglePolygon(
          newGeopolygon.id,
          newGeopolygon.name,
          getShapePopulationFetchOptions(newGeopolygon)
        )
      } else {
        rightSidebarStore.loading = false
      }
    } else {
      rightSidebarStore.loading = false
    }
    this.processing = false
  }

  /**
   * Open the POI menu for a target group and populate it with that group's
   * saved markers only(no Google Places API). This is used both when a
   * target group is selected via the selection observer and when the user
   * clicks an already-selected target group in the Tools panel.
   */
  @action.bound
  async openPoiForTargetGroup (targetGroup) {
    if (!targetGroup || !dataStore || !dataStore.poi) return

    // Always clear POI state when switching to target group view to prevent cross-contamination
    dataStore.poi.temporaryMarkers = []
    dataStore.poi.placeResults = []
    dataStore.poi.bucketById.clear()
    dataStore.poi.sharedBucketsById.clear()
    dataStore.poi.removedFromBucketById.clear()
    dataStore.poi.pendingBucketById.clear()
    dataStore.poi.disabledRowsById.clear()
    dataStore.poi.general = []
    dataStore.poi.allies = []
    dataStore.poi.competitors = []
    dataStore.poi.hasBucketChanges = false
    dataStore.poi.currentTargetGroupId = null

    await targetGroup.loadSavedMarkers()

    const tempMarkers = (targetGroup.savedMarkers || [])
      .filter(sm => sm.position && sm.position.latitude != null && sm.position.longitude != null)
      .map(sm => ({
        id: `saved_${sm.id}`,
        savedMarkerId: sm.id,
        position: { latLng: { lat: sm.position.latitude, lng: sm.position.longitude } },
        name: sm.name || '',
        address: sm.description || sm.address || '',
        type: (sm.types && typeof sm.types === 'string') ? sm.types : '',
        types: sm.types || '',
        getColor: sm.color_id || 1,
        color_id: sm.color_id || 1,
        icon_url: sm.icon_url || null,
        is_general: sm.is_general,
        is_ally: sm.is_ally,
        is_competitor: sm.is_competitor
      }))

    dataStore.poi.temporaryMarkers = tempMarkers
    dataStore.poi.placeResults = []
    dataStore.poi.currentTargetGroupId = targetGroup.pk
    dataStore.poi.syncBucketsFromSavedMarkersForTargetGroup(targetGroup)

    // Don't auto-open POI menu - user should open it explicitly via search input, poi button, or View More button
  }

  async addGeopolygonIsoChrone (group, shape, geo) {
    this.clearPreviousSelectionBeforeAdd()
    const realGroup = this.resolveTargetGroup(group)
    this.isPOIMenuExpanded = true
    this.setActiveTab('people')
    rightSidebarStore.resetData()
    rightSidebarStore.targetGroupName = shape.name || 'New Shape'
    rightSidebarStore.loading = true

    this.processing = true
    const geopolygon = await shape.add(realGroup.pk, geo)
    if (geopolygon) {
      this.registerStarterShapeCreated()
      await realGroup.loadGeometries(geopolygon.id, geopolygon.task_id)
      await realGroup.loadBoundaries()
      if (group && group !== realGroup) {
        await group.loadGeometries(geopolygon.id, geopolygon.task_id)
        await group.loadBoundaries()
        group.expanded = true
      }
      realGroup.expanded = true
      dataStore.setFilterMaxAndMins(this)
      this.isFilterActive = false
      this.filters.resetFilters()
      const newGeopolygon = _.find(realGroup.geopolygons, { id: geopolygon.id }) || _.find(group.geopolygons, { id: geopolygon.id })
      if (newGeopolygon) {
        selectionStore.clearSelectedMenuItems()
        selectionStore.addSelectedMenuItem(newGeopolygon)
        selectionStore.select(newGeopolygon, { data: { group: realGroup } })
        this.setBoundsToItem(newGeopolygon)
        selectionStore.hideSubTools()
        rightSidebarStore.fetchDataForSinglePolygon(
          newGeopolygon.id,
          newGeopolygon.name,
          getShapePopulationFetchOptions(newGeopolygon)
        )
      } else {
        rightSidebarStore.loading = false
      }
    } else {
      rightSidebarStore.loading = false
    }
    this.processing = false
  }

  async addBoundary (group, boundaryList) {
    this.clearPreviousSelectionBeforeAdd()
    const realGroup = this.resolveTargetGroup(group)
    if (realGroup.findBoundary(boundaryList.selectedBoundary.id)) {
      boundaryList.error = i18next.t('GLOBAL.DUPLICATE_BOUNDARY')
      return
    }

    this.processing = true
    const boundary = await boundaryList.selectedBoundary.add(realGroup.pk)
    if (boundary) {
      this.registerStarterShapeCreated()
      await realGroup.loadBoundaries()
      await realGroup.loadZipCodeBoundaries()
      await realGroup.loadGeometries()
      if (group && group !== realGroup) {
        await group.loadBoundaries()
        await group.loadZipCodeBoundaries()
        await group.loadGeometries()
        group.expanded = true
      }
      realGroup.expanded = true
      dataStore.setFilterMaxAndMins(this)
      this.isFilterActive = false
      this.filters.resetFilters()
      const newBoundary = _.find(realGroup.boundaries, { remote_db_id: boundary.remote_db_id }) ||
        _.find(realGroup.zipCodeBoundaries, { remote_db_id: boundary.remote_db_id }) ||
        _.find(group.boundaries, { remote_db_id: boundary.remote_db_id }) ||
        _.find(group.zipCodeBoundaries, { remote_db_id: boundary.remote_db_id })
      if (newBoundary) {
        selectionStore.select(newBoundary, { data: { group: realGroup } })
        this.setBoundsToItem(newBoundary)
        selectionStore.hideSubTools()
      }
    }
    this.processing = false
  }

  async addMarker (group, marker) {
    this.clearPreviousSelectionBeforeAdd()
    this.processing = true
    const realGroup = this.resolveTargetGroup(group)
    // Add marker with is_general=true by default
    const remoteMarker = await marker.add(realGroup.pk, true)
    if (remoteMarker) {
      await realGroup.loadSavedMarkers()
      if (group && group !== realGroup) {
        await group.loadSavedMarkers()
        group.expanded = true
      }
      realGroup.expanded = true
      dataStore.setFilterMaxAndMins(this)
      this.isFilterActive = false
      this.filters.resetFilters()

      const savedMarker = _.find(realGroup.savedMarkers, { id: remoteMarker.id }) || _.find(group.savedMarkers, { id: remoteMarker.id })

      // Force refresh of POI state by clearing currentTargetGroupId
      if (dataStore.poi) {
        dataStore.poi.currentTargetGroupId = null
      }

      // Select the newly created marker(this will trigger zoom logic via observer)
      // The zoom logic in setBoundsToItem will respect user's current zoom if they're zoomed in
      if (savedMarker) {
        selectionStore.clearSelectedMenuItems()
        selectionStore.addSelectedMenuItem(savedMarker)
        selectionStore.select(savedMarker, { data: { group: realGroup } })
        this.setBoundsToItem(savedMarker)
        selectionStore.hideSubTools()
      } else {
        // Fallback: select the target group to trigger POI refresh with the new marker
        selectionStore.select(realGroup)
        selectionStore.hideSubTools()
      }

      this.addNotification({
        text: `Marker "${savedMarker ? savedMarker.name : 'New marker'}" added to ${realGroup.name}`,
        isError: false,
        category: 'Success'
      })
    }
    this.processing = false
  }

  async editMarker (group, marker) {
    this.processing = true
    const realGroup = this.resolveTargetGroup(group)
    const remoteMarker = await marker.edit(realGroup.pk)
    if (remoteMarker) {
      await realGroup.loadSavedMarkers()
      if (group && group !== realGroup) {
        await group.loadSavedMarkers()
      }
      dataStore.setFilterMaxAndMins(this)
      this.isFilterActive = false
      this.filters.resetFilters()

      const markerInState = _.find(realGroup.savedMarkers, { id: remoteMarker.id }) || _.find(group.savedMarkers, { id: remoteMarker.id })

      if (markerInState) {
        markerInState.color_id = remoteMarker.color
      }

      selectionStore.select(
        markerInState || _.find(realGroup.savedMarkers, { id: remoteMarker.id }),
        { data: { group: realGroup } }
      )
    }
    this.processing = false
  }

  async addUserShapefileShape (group, userShapefileList) {
    this.processing = true
    const realGroup = this.resolveTargetGroup(group)
    const geopolygon = await userShapefileList.selectedShape.add(realGroup.pk)
    if (geopolygon) {
      await realGroup.loadGeometries()
      if (group && group !== realGroup) {
        await group.loadGeometries()
      }
    }
    this.processing = false
  }

  async addUserShapefileShapes (group, userShapefile) {
    this.processing = true
    const realGroup = this.resolveTargetGroup(group)
    const geopolygons = await userShapefile.addMultiple(realGroup.pk)
    userShapefile.deselectAll()
    if (geopolygons.length > 0) {
      await realGroup.loadGeometries()
      if (group && group !== realGroup) {
        await group.loadGeometries()
      }
    }
    this.processing = false
  }

  async renameGeopolygon (group, geopolygon, name) {
    if (name === '') {
      geopolygon.error = i18next.t('GLOBAL.EMPTY_SHAPE_NAME')
      return
    }

    this.processing = true
    const realGroup = this.resolveTargetGroup(group)
    await geopolygon.rename(name)
    await realGroup.loadGeometries()
    if (group && group !== realGroup) {
      await group.loadGeometries()
    }
    this.processing = false
  }

  async updateGeopolygon (group, shape, newShape) {
    this.processing = true
    const realGroup = dataStore.targetGroups.find(tg => tg.pk === group.pk) || group
    if (selectionStore.type === selectionTypes.GEOPOLYGON || shape.validate()) {
      if (selectionStore.type === selectionTypes.GEOPOLYGON || selectionStore.type === selectionTypes.ISOCHRONE) {
        const updatedGeopolygon = await shape.updateShape(newShape)
        if (updatedGeopolygon) {
          const geopolygon = (realGroup.geopolygons && realGroup.geopolygons.find(p => (shape.id != null && p.id === shape.id) || (shape.inner_id != null && p.inner_id === shape.inner_id) || p === shape)) || shape
          if (geopolygon && typeof geopolygon.updateGeoJSON === 'function' && updatedGeopolygon.polygon) {
            geopolygon.updateGeoJSON(updatedGeopolygon.polygon)
          }
          selectionStore.select(geopolygon, { data: { group: realGroup } }, false, true)
        } else {
          selectionStore.select(selectionStore.item, { data: { group: realGroup } }, false, true)
        }
      } else if (selectionStore.type === selectionTypes.POINT_BUFFER || selectionStore.type === selectionTypes.LINE) {
        const updatedGeopolygon = await shape.updateShape()
        const geopolygon = (realGroup.geopolygons && realGroup.geopolygons.find(p => (shape.id != null && p.id === shape.id) || (shape.inner_id != null && p.inner_id === shape.inner_id) || p === shape)) || shape
        if (geopolygon && typeof geopolygon.updateGeoJSON === 'function' && updatedGeopolygon && updatedGeopolygon.polygon) {
          geopolygon.updateGeoJSON(updatedGeopolygon.polygon)
        }
        selectionStore.select(geopolygon, { data: { group: realGroup } }, false, true)
      }
      selectionStore.disableEdit()
      if (typeof realGroup.loadGeometries === 'function') {
        await realGroup.loadGeometries()
      }
      dataStore.setFilterMaxAndMins(this)
    }
    this.processing = false
  }

  geometryPayload (items, destinationGroup) {
    const payload = {
      target_group_id: destinationGroup.pk,
      geo_polygon_ids: [],
      marker_ids: [],
      boundary_ids: [],
      zip_code_boundary_ids: []
    }
    items.forEach(item => {
      if (item instanceof Geopolygon) {
        payload.geo_polygon_ids.push(item.id)
      } else if (item instanceof SavedMarker) {
        payload.marker_ids.push(item.id)
      } else if (item instanceof Boundary) {
        (item.isZipCode ? payload.zip_code_boundary_ids : payload.boundary_ids).push(item.inner_id)
      }
    })
    return payload
  }

  sourceGroupOf (item) {
    if (item && item.targetGroupId != null) {
      return this.resolveTargetGroup({ pk: item.targetGroupId })
    }
    const groups = [...(dataStore.targetGroups || []), ...(dataStore.sharedTargetGroups || [])]
    return groups.find(group => (group.savedMarkers || []).includes(item))
  }

  async reloadGroupItems (group, items) {
    if (!group) return
    const has = type => items.some(item => item instanceof type)
    if (has(Geopolygon)) await group.loadGeometries()
    if (has(SavedMarker)) await group.loadSavedMarkers()
    if (items.some(item => item instanceof Boundary && !item.isZipCode)) await group.loadBoundaries()
    if (items.some(item => item instanceof Boundary && item.isZipCode)) await group.loadZipCodeBoundaries()
  }

  async moveItemsToGroup (items, destinationGroup, sourceGroups = []) {
    const sources = sourceGroups.length ? sourceGroups : items.map(item => this.sourceGroupOf(item))
    const response = await api.moveGeometryBulk(this.geometryPayload(items, destinationGroup))
    const moved = (response && response.data && response.data.moved) || []
    const failed = (response && response.data && response.data.failed) || []
    const groups = _.uniqBy([destinationGroup, ...sources.map(group => this.resolveTargetGroup(group) || group)].filter(Boolean), 'pk')
    await Promise.all(groups.map(group => this.reloadGroupItems(group, items)))
    if (!moved.length && failed.length) {
      throw new Error(failed[0].error)
    }
    return { moved, failed }
  }

  notifyPartialMove (failed) {
    if (!failed || !failed.length) return
    this.addNotification({
      text: i18next.t('POI.MOVE_TO_FOLDER_ERROR', {
        error: failed.map(item => item.error).join(', '),
        defaultValue: `Failed to move ${failed.length} item(s): ${failed.map(item => item.error).join(', ')}`
      }),
      isError: true,
      category: 'Error'
    })
  }

  async moveGeopolygon (geopolygon, sourceGroup) {
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.MOVE_NO_DESTINATION', { defaultValue: 'Please select a folder to move to.' }),
        isError: true,
        category: 'Error'
      })
      return
    }
    const realSourceGroup = this.resolveTargetGroup(sourceGroup) || sourceGroup
    const name = (geopolygon && geopolygon.name) || i18next.t('GLOBAL.GEOPOLYGON')
    const loaderId = this.addNotification({
      text: i18next.t('POI.MOVING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Moving ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let moved = false
    try {
      await this.moveItemsToGroup([geopolygon], destinationGroup, [realSourceGroup])
      destinationGroup.expanded = true
      selectionStore.clear()
      moved = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_ERROR', {
          error: (error && error.message) || 'Unknown error',
          defaultValue: `Failed to move: ${(error && error.message) || 'Unknown error'}`
        }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    if (moved) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_SUCCESS_NAME', {
          name,
          folderName: destinationGroup.name,
          defaultValue: `${name} moved to ${destinationGroup.name}`
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async moveItemsOnDrop (item, group) {
    if (item instanceof Geopolygon) {
      await this.moveGeopolygonOnDrop(item, group)
    } else if (item instanceof SavedMarker) {
      await this.moveMarkerOnDrop(item, group)
    } else if (item instanceof Boundary) {
      await this.moveBoundaryOnDrop(item, group)
    }
  }

  async moveOnDrop (item, group) {
    group.processing = true
    try {
      await this.moveItemsToGroup([item], group, [this.draggedMenuItemSourceGroup])
      selectionStore.clearSelectedMenuItems()
    } catch (error) {
      this.notifyPartialMove([{ error: (error && error.message) || 'Unknown error' }])
    } finally {
      group.processing = false
    }
  }

  async moveGeopolygonOnDrop (geopolygon, group) {
    await this.moveOnDrop(geopolygon, group)
  }

  async copyGeopolygon (geopolygon, sourceGroup) {
    // Popup menus pass display clones from getFilteredGroups(). Look up the
    // real TargetGroup so loadGeometries/expanded write through to the folder list.
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.COPY_NO_DESTINATION'),
        isError: true,
        category: 'Error'
      })
      return
    }
    const name = (geopolygon && geopolygon.name) || i18next.t('GLOBAL.GEOPOLYGON')
    const loaderId = this.addNotification({
      text: i18next.t('POI.COPYING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Copying ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let copied = false
    try {
      await geopolygon.copy(destinationGroup.pk)
      // Refresh destination shapes so the copy appears without a page reload.
      await destinationGroup.loadGeometries()
      // ItemList only renders when the folder is expanded.
      destinationGroup.expanded = true
      copied = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_ERROR', { error: (error && error.message) || 'Unknown error' }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    // Toast after processing clears so it is not covered by the loading state.
    if (copied) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_SUCCESS', {
          name,
          folderName: destinationGroup.name
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async copyGeopolygonBulk () {
    // Multi-select Copy uses the same toast + folder refresh as single shape copy.
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.COPY_NO_DESTINATION'),
        isError: true,
        category: 'Error'
      })
      return
    }
    const loaderId = this.addNotification({
      text: i18next.t('POI.COPYING_ITEMS_TO_FOLDER', {
        folderName: destinationGroup.name,
        defaultValue: `Copying items to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    const items = selectionStore.selectedMenuItems.filter(item => !(item instanceof TargetGroup))
    destinationGroup.processing = true
    let copied = false
    try {
      const response = await api.copyGeopolygonBulk(this.geometryPayload(items, destinationGroup))
      const success = (response && response.data && response.data.success) || []
      const failed = (response && response.data && response.data.failed) || []
      await this.reloadGroupItems(destinationGroup, items)
      if (!success.length && failed.length) {
        throw new Error(failed[0].error)
      }
      destinationGroup.expanded = true
      selectionStore.clearSelectedMenuItems()
      this.notifyPartialMove(failed)
      copied = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_ERROR', { error: (error && error.message) || 'Unknown error' }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
    }
    if (copied) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_ITEMS_TO_FOLDER_SUCCESS', { folderName: destinationGroup.name }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async deleteGeoBulk () {
    const data = {
      geopolygon_ids: selectionStore.selectedGeopolygonIds,
      marker_ids: selectionStore.selectedMarkerIds,
      admin_boundary_ids: selectionStore.selectedBoundaryIds,
      zip_code_boundary_ids: selectionStore.selectedZipCodeBoundaryIds,
      target_group_ids: selectionStore.selectedTargetGroupIds
    }

    const affectedTargetGroupIds = []
    selectionStore.selectedMenuItems.forEach(item => {
      if (item && !(item instanceof TargetGroup)) {
        affectedTargetGroupIds.push(item.targetGroupId)
      }
    })

    this.processing = true
    await api.deleteGeoBulk({ data })
    dataStore.reloadTargetGroups(affectedTargetGroupIds)
    await this.refreshMenu()
    this.hideDeletePrompt()
    this.processing = false
  }

  async moveGeoBulk (sourceGroup) {
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.MOVE_NO_DESTINATION', { defaultValue: 'Please select a folder to move to.' }),
        isError: true,
        category: 'Error'
      })
      return
    }
    const loaderId = this.addNotification({
      text: i18next.t('POI.MOVING_ITEMS_TO_FOLDER', {
        folderName: destinationGroup.name,
        defaultValue: `Moving items to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    const items = selectionStore.selectedMenuItems.filter(item => !(item instanceof TargetGroup))

    destinationGroup.processing = true
    let moved = false
    try {
      const { failed } = await this.moveItemsToGroup(items, destinationGroup)
      selectionStore.clear()
      selectionStore.clearSelectedMenuItems()
      destinationGroup.expanded = true
      this.notifyPartialMove(failed)
      moved = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_ERROR', {
          error: (error && error.message) || 'Unknown error',
          defaultValue: `Failed to move: ${(error && error.message) || 'Unknown error'}`
        }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
    }
    if (moved) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_ITEMS_TO_FOLDER_SUCCESS', {
          folderName: destinationGroup.name,
          defaultValue: `Items moved to folder: ${destinationGroup.name}`
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async moveGeoBulkOnDrop (targetGroup) {
    const items = selectionStore.selectedMenuItems.filter(item => !(item instanceof TargetGroup))
    targetGroup.processing = true
    try {
      const { failed } = await this.moveItemsToGroup(items, targetGroup)
      selectionStore.clear()
      selectionStore.clearSelectedMenuItems()
      this.notifyPartialMove(failed)
    } catch (error) {
      this.notifyPartialMove([{ error: (error && error.message) || 'Unknown error' }])
    } finally {
      targetGroup.processing = false
    }
  }

  async refreshMenu () {
    await dataStore.loadTargetGroups()
    selectionStore.clear()
    selectionStore.clearSelectedMenuItems()
  }

  async moveMarker (marker, sourceGroup) {
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.MOVE_NO_DESTINATION', { defaultValue: 'Please select a folder to move to.' }),
        isError: true,
        category: 'Error'
      })
      return
    }
    const realSourceGroup = this.resolveTargetGroup(sourceGroup) || sourceGroup
    const name = (marker && marker.name) || i18next.t('GLOBAL.MARKER')
    const loaderId = this.addNotification({
      text: i18next.t('POI.MOVING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Moving ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let moved = false
    try {
      await this.moveItemsToGroup([marker], destinationGroup, [realSourceGroup])
      // Expand the destination group so the moved marker is visible
      destinationGroup.expanded = true
      selectionStore.clear()
      moved = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_ERROR', {
          error: (error && error.message) || 'Unknown error',
          defaultValue: `Failed to move: ${(error && error.message) || 'Unknown error'}`
        }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    if (moved) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_SUCCESS_NAME', {
          name,
          folderName: destinationGroup.name,
          defaultValue: `${name} moved to ${destinationGroup.name}`
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async moveMarkerOnDrop (marker, group) {
    await this.moveOnDrop(marker, group)
  }

  async copyMarker (marker, sourceGroup) {
    // Same as copyGeopolygon: use the real folder, reload markers, expand, then toast.
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.COPY_NO_DESTINATION'),
        isError: true,
        category: 'Error'
      })
      return
    }
    const name = (marker && marker.name) || i18next.t('GLOBAL.MARKER')
    const loaderId = this.addNotification({
      text: i18next.t('POI.COPYING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Copying ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let copied = false
    try {
      await marker.copy(destinationGroup.pk)
      await destinationGroup.loadSavedMarkers()
      destinationGroup.expanded = true
      copied = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_ERROR', { error: (error && error.message) || 'Unknown error' }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    if (copied) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_SUCCESS', {
          name,
          folderName: destinationGroup.name
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async moveBoundaryOnDrop (boundary, group) {
    await this.moveOnDrop(boundary, group)
  }

  async moveBoundary (boundary, sourceGroup) {
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.MOVE_NO_DESTINATION', { defaultValue: 'Please select a folder to move to.' }),
        isError: true,
        category: 'Error'
      })
      return
    }
    const realSourceGroup = this.resolveTargetGroup(sourceGroup) || sourceGroup
    const name = (boundary && boundary.name) || i18next.t('BOUNDARY.BOUNDARY')
    const loaderId = this.addNotification({
      text: i18next.t('POI.MOVING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Moving ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let moved = false
    try {
      await this.moveItemsToGroup([boundary], destinationGroup, [realSourceGroup])
      // Expand the destination group so the moved boundary is visible
      destinationGroup.expanded = true
      selectionStore.clear()
      moved = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_ERROR', {
          error: (error && error.message) || 'Unknown error',
          defaultValue: `Failed to move: ${(error && error.message) || 'Unknown error'}`
        }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    if (moved) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.MOVE_TO_FOLDER_SUCCESS_NAME', {
          name,
          folderName: destinationGroup.name,
          defaultValue: `${name} moved to ${destinationGroup.name}`
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async copyBoundary (boundary, sourceGroup) {
    // Same as copyGeopolygon: use the real folder, reload boundaries, expand, then toast.
    const destinationGroup = this.resolveTargetGroup(dataStore.copyDestinationGroup)
    if (!destinationGroup) {
      this.addNotification({
        text: i18next.t('POI.COPY_NO_DESTINATION'),
        isError: true,
        category: 'Error'
      })
      return
    }
    const name = (boundary && boundary.name) || i18next.t('BOUNDARY.BOUNDARY')
    const loaderId = this.addNotification({
      text: i18next.t('POI.COPYING_TO_FOLDER', {
        name,
        folderName: destinationGroup.name,
        defaultValue: `Copying ${name} to ${destinationGroup.name}...`
      }),
      isLoading: true
    })
    this.processing = true
    destinationGroup.processing = true
    let copied = false
    try {
      await boundary.copy(destinationGroup.pk)
      await destinationGroup.loadBoundaries()
      destinationGroup.expanded = true
      copied = true
    } catch (error) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_ERROR', { error: (error && error.message) || 'Unknown error' }),
        isError: true,
        category: 'Error'
      })
    } finally {
      destinationGroup.processing = false
      this.processing = false
    }
    if (copied) {
      this.clearNotification(loaderId)
      this.addNotification({
        text: i18next.t('POI.COPY_TO_FOLDER_SUCCESS', {
          name,
          folderName: destinationGroup.name
        }),
        isError: false,
        category: 'Success'
      })
    }
  }

  async uploadUserShapefile (userShapefileList) {
    this.processing = true
    await userShapefileList.upload()
    this.processing = false
  }

  async uploadUserMarkerfile (userMarkerfileList) {
    this.processing = true
    await userMarkerfileList.upload()
    this.processing = false
  }

  async changePolygonColor (item = selectionStore.item) {
    this.processing = true
    try {
      await api.patchGeopolygon(item.id, { color: selectionStore.selectedColor, color_transparency: item.transparency })
      await dataStore.refreshRecentColors()

      const groupItem = (selectionStore._menuItem && selectionStore._menuItem.data && selectionStore._menuItem.data.group) || (selectionStore.selectedTargetGroup && selectionStore.selectedTargetGroup.item)
      if (groupItem) await groupItem.load()
    } catch (err) {
      console.warn(err)
    } finally {
      this.processing = false
      selectionStore.hidePopupMenu()
      selectionStore._isEditing = false
    }
  }

  async changeBoundaryColor (item = selectionStore.item) {
    this.processing = true
    try {
      const { selectedColor } = selectionStore
      const data = { color: selectedColor, color_transparency: item.transparency }
      if (item.isZipCode) {
        await api.patchZipCodeBoundaryColor(item.inner_id, data)
      } else {
        await api.patchBoundaryColor(item.inner_id, data)
      }
      runInAction(() => {
        item.color_id = selectedColor
      })
      await dataStore.refreshRecentColors()

      const groupItem = (selectionStore._menuItem && selectionStore._menuItem.data && selectionStore._menuItem.data.group) ||
        (selectionStore.data && selectionStore.data.group) ||
        (selectionStore.selectedTargetGroup && selectionStore.selectedTargetGroup.item) ||
        (item && item.targetGroupId && dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(item.targetGroupId)))
      if (groupItem) {
        if (item.isZipCode) {
          await groupItem.loadZipCodeBoundaries()
        } else {
          await groupItem.loadBoundaries()
        }
      }
    } catch (err) {
      console.warn(err)
    } finally {
      this.processing = false
      selectionStore.hidePopupMenu()
      selectionStore._isEditing = false
    }
  }

  async changePolygonColorsInGroup (item = selectionStore.item) {
    this.processing = true
    try {
      await api.patchGeopolygonColor(item.pk, { color_id: selectionStore.selectedColor, color_transparency: item.transparency })
      await dataStore.refreshRecentColors()
      runInAction(() => {
        if (item.boundaries) item.boundaries.forEach(b => { b.color_id = selectionStore.selectedColor })
        if (item.zipCodeBoundaries) item.zipCodeBoundaries.forEach(z => { z.color_id = selectionStore.selectedColor })
        if (item.geopolygons) item.geopolygons.forEach(p => { p.color_id = selectionStore.selectedColor })
      })
      await item.load()
    } catch (err) {
      console.warn(err)
    } finally {
      this.processing = false
      selectionStore.hidePopupMenu()
      selectionStore._isEditing = false
    }
  }

  async getAutoComplete (input) {
    const payload = {
      includeQueryPredictions: true,
      input,
      locationBias: {
        circle: {
          center: {
            latitude: uiStore.mapCenterInfo.lat,
            longitude: uiStore.mapCenterInfo.lng
          },
          radius: 5000
        }
      }
    }
    await dataStore.poi.queryAndSetPredictions(payload)
  }

  async searchNearbyPlaces () {
    if (dataStore && dataStore.poi) dataStore.poi.loading = true
    const poi = dataStore && dataStore.poi
    const preserved = poi && poi.captureClassificationState ? poi.captureClassificationState() : null
    try {
      // Ensure the POI(Point of Interest) menu is open so results can be displayed
      // If the menu is closed, open it automatically
      if (!this.isPOIMenuExpanded) {
        this.isPOIMenuExpanded = true
      }

      // Initialize an empty array to store all the places we'll get from the API
      const allPlaces = []

      // Create a Set to track place IDs we've already seen(to avoid duplicates)
      // Set is used because checking if an ID exists is very fast(O(1) lookup)
      const seenPlaceIds = new Set()

      //  Initialize variable to store the visible map bounds(with margin) for filtering results later
      let visibleBounds = null
      let center = null
      let radius = null

      // Check if a shape is selected(GEOPOLYGON, POINT_BUFFER, LINE, or BOUNDARY)
      const selectedShape = selectionStore && selectionStore.item
      const isShapeSelected = selectedShape && selectedShape.geoJSON && (
        selectionStore.type === selectionTypes.GEOPOLYGON ||
        selectionStore.type === selectionTypes.POINT_BUFFER ||
        selectionStore.type === selectionTypes.LINE ||
        selectionStore.type === selectionTypes.BOUNDARY
      )

      if (isShapeSelected && typeof selectedShape.bounds === 'function') {
        // Use shape bounds for search
        const shapeBounds = selectedShape.bounds()
        if (shapeBounds && shapeBounds[0] && shapeBounds[1]) {
          const sw = { lat: shapeBounds[0][0], lng: shapeBounds[0][1] }
          const ne = { lat: shapeBounds[1][0], lng: shapeBounds[1][1] }
          // Store visible bounds for filtering
          visibleBounds = {
            ne: { lat: ne.lat, lng: ne.lng },
            sw: { lat: sw.lat, lng: sw.lng }
          }
          // Calculate center from shape bounds
          center = {
            lat: (sw.lat + ne.lat) / 2,
            lng: (sw.lng + ne.lng) / 2
          }
          // Calculate radius from shape bounds using Haversine formula
          const toRad = (deg) => deg * Math.PI / 180
          const R = 6371000 // Earth radius in meters
          const dLat = toRad(ne.lat - center.lat)
          const dLng = toRad(ne.lng - center.lng)
          const la1 = toRad(center.lat)
          const la2 = toRad(ne.lat)
          const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
          const calculatedRadius = Math.max(50, Math.ceil(2 * R * Math.asin(Math.sqrt(h))))
          // Ensure radius is within API limits [100, 50000] meters
          radius = Math.max(100, Math.min(calculatedRadius, 50000))
        }
      }

      // If no shape selected, use visible map area(between sidebars)
      if (!center || !radius) {
        // Safety check - if the map doesn't exist or isn't ready, exit early
        // This prevents errors if the function is called before the map is loaded
        if (!this.map || !this.map.leafletElement) {
          return
        }

        const leafletElement = this.map.leafletElement

        // Get visible map area(accounting for sidebars)
        // This ensures results appear in the center of visible map, not hidden under sidebars
        const size = leafletElement.getSize()
        let paddingTopLeft = this.boundsTopLeftOffset || { x: 0, y: 0 }
        const paddingBottomRight = this.boundsBottomRightOffset || { x: 0, y: 0 }

        // Fallback: If boundsTopLeftOffset is not set or is(0,0), calculate left sidebar width directly
        // This handles the case when the page initially loads and offsets haven't been calculated yet
        if ((!paddingTopLeft.x && !paddingTopLeft.y) || (paddingTopLeft.x === 0 && paddingTopLeft.y === 0)) {
          const leftSidebarWidth = this.menuWidth || this.toolsWidth || 332 // Default to 332px if not available
          paddingTopLeft = { x: leftSidebarWidth, y: 0 }
        }

        // Calculate right sidebar(POI menu) width
        const windowWidth = window.innerWidth || size.x
        const poiMenuWidth = this.isPOIMenuExpanded
          ? Math.min(windowWidth * 0.29, 555) // 29vw with max 555px
          : 56 // Button strip width when collapsed

        // Calculate the actual visible bounds(the area between sidebars)
        const visibleTopLeft = new Point(paddingTopLeft.x, paddingTopLeft.y)
        const visibleBottomRight = new Point(
          size.x - paddingBottomRight.x - poiMenuWidth,
          size.y - paddingBottomRight.y
        )

        // Convert visible area corners to lat/lng
        const visibleTopLeftLatLng = leafletElement.containerPointToLatLng(visibleTopLeft)
        const visibleBottomRightLatLng = leafletElement.containerPointToLatLng(visibleBottomRight)

        // Create visible bounds from the actual visible area(not full map)
        const visibleNe = {
          lat: Math.max(visibleTopLeftLatLng.lat, visibleBottomRightLatLng.lat),
          lng: Math.max(visibleTopLeftLatLng.lng, visibleBottomRightLatLng.lng)
        }
        const visibleSw = {
          lat: Math.min(visibleTopLeftLatLng.lat, visibleBottomRightLatLng.lat),
          lng: Math.min(visibleTopLeftLatLng.lng, visibleBottomRightLatLng.lng)
        }

        // Store the visible bounds for later filtering
        visibleBounds = {
          ne: { lat: visibleNe.lat, lng: visibleNe.lng },
          sw: { lat: visibleSw.lat, lng: visibleSw.lng }
        }

        // Calculate center of VISIBLE map area(between sidebars), not full map
        center = {
          lat: (visibleSw.lat + visibleNe.lat) / 2,
          lng: (visibleSw.lng + visibleNe.lng) / 2
        }

        // Get all 4 corners of the visible bounds
        const corners = [
          visibleNe,
          { lat: visibleNe.lat, lng: visibleSw.lng }, // NW
          { lat: visibleSw.lat, lng: visibleNe.lng }, // SE
          visibleSw
        ]

        // Haversine formula helper functions
        const toRad = (deg) => deg * Math.PI / 180
        const R = 6371000 // Earth radius in meters

        // Calculate distance between two lat/lng points using Haversine formula
        const distance = (a, b) => {
          const dLat = toRad(b.lat - a.lat)
          const dLng = toRad(b.lng - a.lng)
          const x =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(a.lat)) *
            Math.cos(toRad(b.lat)) *
            Math.sin(dLng / 2) ** 2
          return 2 * R * Math.asin(Math.sqrt(x))
        }

        // Find the farthest corner from center to ensure complete coverage
        const calculatedRadius = Math.max(
          ...corners.map((corner) => distance(center, corner))
        )

        // Use calculatedRadius directly, but ensure it's within API limits [100, 50000] meters
        // If calculatedRadius exceeds 50000, use the maximum allowed value(50000)
        // If calculatedRadius is less than 100, use the minimum allowed value(100)
        radius = Math.max(100, Math.min(calculatedRadius, 50000))

        // Round to nearest integer for cleaner API requests
        radius = Math.ceil(radius)

        // Debug logging to verify radius calculation
        console.log('Search radius calculation:', {
          calculatedRadius: Math.ceil(calculatedRadius),
          finalRadius: radius,
          isCapped: calculatedRadius > 50000
        })
      }

      // Build the API payload with circle format
      const payload = {
        // Maximum number of results to return(API limit is 1-20)
        // This is the total limit, not per-cell like before
        maxResultCount: 20,

        // Sort results by popularity(most popular places first)
        // Other options could be 'DISTANCE' but popularity usually gives better results
        rankPreference: 'POPULARITY',

        //  Location restriction using circle format(required by searchNearby API)
        // The circle covers the entire visible map area
        locationRestriction: {
          circle: {
            center: {
              latitude: center.lat, // Center latitude of visible area
              longitude: center.lng // Center longitude of visible area
            },
            radius // Radius in meters to cover entire visible rectangle
          }
        }
      }

      //  Make the SINGLE API call to Google Places APIFa
      // This replaces the 9 parallel calls we had before
      try {
        // Call the API with our payload(single request instead of 9)
        const response = await api.getGoogleNearbyPlaces(payload)

        // Check if the API response was successful(status 200-299)
        // If not successful, we'll skip processing and show no results
        if (response.ok) {
          this.isLimitExceeded = false
          this.limitExceededMessage = ''
          // Parse the JSON response from the API
          // The response contains a 'places' array with all the nearby places
          const data = await response.json()

          // Extract the places array from the response
          // Use Array.isArray check for safety - if places is not an array, default to empty array
          const places = Array.isArray(data.places) ? data.places : []

          // LINE 45-60: Filter and validate each place from the API response
          for (const place of places) {
            // Skip places that don't have valid location data
            // Every place must have latitude and longitude to be displayed on the map
            if (!place.location || !place.location.latitude || !place.location.longitude) {
              continue // Skip this place, move to next one
            }

            // Extract latitude and longitude for easier use
            const placeLat = place.location.latitude
            const placeLng = place.location.longitude

            // Check if the place is within our visible bounds(expanded for better results)
            // Since we're using radius-based search, we can be more lenient with bounds filtering
            // Expand bounds by 20% to include places slightly outside visible area but still relevant
            if (visibleBounds) {
              const boundsLatDiff = visibleBounds.ne.lat - visibleBounds.sw.lat
              const boundsLngDiff = visibleBounds.ne.lng - visibleBounds.sw.lng
              const expandedMargin = 0.2 // 20% expansion

              const expandedBounds = {
                ne: {
                  lat: visibleBounds.ne.lat + (boundsLatDiff * expandedMargin),
                  lng: visibleBounds.ne.lng + (boundsLngDiff * expandedMargin)
                },
                sw: {
                  lat: visibleBounds.sw.lat - (boundsLatDiff * expandedMargin),
                  lng: visibleBounds.sw.lng - (boundsLngDiff * expandedMargin)
                }
              }

              // Only filter if place is clearly outside expanded bounds
              if (placeLat < expandedBounds.sw.lat || placeLat > expandedBounds.ne.lat ||
                placeLng < expandedBounds.sw.lng || placeLng > expandedBounds.ne.lng) {
                continue // Place is outside expanded visible area, skip it
              }
            }

            // Create a unique ID for this place
            // Use the place's ID if available, otherwise create one from coordinates
            const placeId = place.id || `${placeLat},${placeLng}`

            // Check for duplicates and add to results
            // Only add places we haven't seen before(avoid duplicates)
            if (placeId && !seenPlaceIds.has(placeId)) {
              seenPlaceIds.add(placeId) // Mark this ID as seen
              allPlaces.push(place) // Add to our results array
            }
          }
        } else {
          // Handle error responses(including limit exceeded)
          const errorData = await response.json()

          // Check if limit exceeded
          if (response.status === 403 && errorData.error === 'limit_exceeded') {
            // Show error message to user
            // this.addNotification({
            //   text: errorData.message || 'You have reached your POI nearby search limit for this month.',
            //   isError: true,
            //   category: '',
            //   timeout: 8000
            // })
            this.isLimitExceeded = true
            this.limitExceededMessage = errorData.message || ''
            return
          }

          // Other errors
          console.warn('POI nearby search error:', errorData)
          this.setStatusMessage(
            'Failed to search nearby places. Please try again.',
            'error',
            5000
          )
          return
        }
      } catch (error) {
        // Handle any errors that occur during the API call
        // Log the error for debugging but don't crash the app
        console.warn('Error searching nearby places:', error)
        // allPlaces will remain empty, so no results will be shown
      }

      // If no places were found, clear the results and exit
      // This ensures the UI shows "no results" instead of old results
      // If no places were found...
      if (!allPlaces || allPlaces.length === 0) {
        dataStore.poi.placeResults = [] // Clear search results list

        // Check if a target group is selected
        const isTargetGroupSelected = selectionStore && selectionStore.type === selectionTypes.TARGET_GROUP
        if (isTargetGroupSelected && selectionStore.item) {
          // If we have a target group, ensure its saved markers are shown
          // This handles cases where API limit is reached but we still want to show saved items
          await this.populatePoiResultsForCurrentSelection()
        } else if (poi && poi.replaceTemporaryMarkersPreservingClassified) {
          poi.replaceTemporaryMarkersPreservingClassified([])
        } else {
          dataStore.poi.temporaryMarkers = [] // Clear map markers
        }
        return // Exit function early
      }

      // Save original search bounds BEFORE zooming(for "Search this area" feature)
      // This must happen before setBounds is called to avoid race conditions
      if (this.map && this.map.leafletElement && visibleBounds) {
        // Use visible bounds(the actual area searched) instead of full map bounds
        this.poiSearchOriginalBounds = new LatLngBounds(
          { lat: visibleBounds.sw.lat, lng: visibleBounds.sw.lng },
          { lat: visibleBounds.ne.lat, lng: visibleBounds.ne.lng }
        )
        this.showSearchThisAreaButton = false // Hide button initially after new search
        this.isNearbySearchActive = true // Mark that nearby search was performed
        this.poiSearchQuery = '' // Clear text query to indicate nearby search
        this.searchButtonWasShown = false // Reset flag when new search is performed
        // Prevent "Search this area" from re-appearing due to programmatic map movement right after a search(setBounds below)
        this.poiIgnoreMoveEndUntil = Date.now() + 1200

        // Store bounds for visualization rectangle with margin(to ensure it's clearly visible)
        // Add 15% margin to ensure rectangle stays well within visible area and doesn't extend under sidebars
        const latDiff = visibleBounds.ne.lat - visibleBounds.sw.lat
        const lngDiff = visibleBounds.ne.lng - visibleBounds.sw.lng
        const latMargin = latDiff * 0.15
        const lngMargin = lngDiff * 0.15

        const rectNe = {
          lat: visibleBounds.ne.lat - latMargin,
          lng: visibleBounds.ne.lng - lngMargin
        }
        const rectSw = {
          lat: visibleBounds.sw.lat + latMargin,
          lng: visibleBounds.sw.lng + lngMargin
        }

        this.poiSearchBoundsRectangle = {
          sw: { lat: rectSw.lat, lng: rectSw.lng },
          ne: { lat: rectNe.lat, lng: rectNe.lng }
        }
      }

      // Convert the raw Google Places data into the format our app expects
      // serializePlaces formats the data and also creates temporaryMarkers for the map
      const placeResults = dataStore.poi.serializePlaces(allPlaces)

      // Store the formatted results so they can be displayed in the UI
      // This updates the POI search results list that users see
      dataStore.poi.placeResults = placeResults

      // Zoom map to show nearby places from this search (not previously classified markers)
      const searchResultIds = new Set((placeResults || []).map(p => p.id || p.place_id).filter(Boolean))
      if (dataStore && dataStore.poi && dataStore.poi.temporaryMarkers && dataStore.poi.temporaryMarkers.length > 0 && this.map && this.map.leafletElement) {
        const markers = dataStore.poi.temporaryMarkers
        const validMarkers = markers.filter(m =>
          m && m.position && m.position.latLng &&
          typeof m.position.latLng.lat === 'number' &&
          typeof m.position.latLng.lng === 'number' &&
          (searchResultIds.size === 0 || searchResultIds.has(m.id))
        )

        if (validMarkers.length > 0) {
          // Calculate bounds from all marker positions
          const lats = validMarkers.map(m => m.position.latLng.lat)
          const lngs = validMarkers.map(m => m.position.latLng.lng)

          const minLat = Math.min(...lats)
          const maxLat = Math.max(...lats)
          const minLng = Math.min(...lngs)
          const maxLng = Math.max(...lngs)

          // Add padding to bounds so markers aren't at the edge of the viewport
          const latPadding = (maxLat - minLat) * 0.1 || 0.01
          const lngPadding = (maxLng - minLng) * 0.1 || 0.01

          const paddedBounds = [
            [minLat - latPadding, minLng - lngPadding],
            [maxLat + latPadding, maxLng + lngPadding]
          ]

          // Use setBounds to zoom the map to fit all markers
          // This triggers map moveend; ignore it briefly so the button doesn't show without user dragging.
          this.poiIgnoreMoveEndUntil = Date.now() + 1200
          this.setBounds(paddedBounds)
        }
      }
    } finally {
      if (poi && poi.restoreClassificationState) {
        poi.restoreClassificationState(preserved)
      }
      if (dataStore && dataStore.poi) dataStore.poi.loading = false
    }
  }

  async changeMarkerColor (item = selectionStore.item) {
    this.processing = true
    try {
      const currentMarker = item
      const targetGroup = (selectionStore._menuItem && selectionStore._menuItem.data && selectionStore._menuItem.data.group) || (selectionStore.data && selectionStore.data.group)

      await api.patchMarker(currentMarker.id, {
        name: currentMarker.name,
        description: currentMarker.description,
        latitude: currentMarker.position.latitude,
        longitude: currentMarker.position.longitude,
        color: selectionStore.selectedColor
      })

      // Update local marker's color_id immediately for instant UI feedback
      if (currentMarker && selectionStore.selectedColor != null) {
        currentMarker.color_id = selectionStore.selectedColor
      }

      // Update POI temporary markers if they exist(for markers shown in POI menu)
      if (dataStore && dataStore.poi && dataStore.poi.temporaryMarkers) {
        const EPS = 1e-4
        const markerLat = currentMarker.position && currentMarker.position.latitude
        const markerLng = currentMarker.position && currentMarker.position.longitude
        if (markerLat != null && markerLng != null) {
          dataStore.poi.temporaryMarkers.forEach(tempMarker => {
            if (!tempMarker || !tempMarker.position || !tempMarker.position.latLng) return
            const tempLat = tempMarker.position.latLng.lat
            const tempLng = tempMarker.position.latLng.lng
            // Check if this temp marker matches the saved marker(by savedMarkerId or lat/lng)
            const idStr = String(tempMarker.id)
            const savedMarkerId = tempMarker.savedMarkerId || (idStr.startsWith('saved_') ? parseInt(idStr.replace('saved_', ''), 10) : null)
            const matchesById = savedMarkerId === currentMarker.id
            const matchesByPosition = tempLat != null && tempLng != null &&
              Math.abs(tempLat - markerLat) < EPS && Math.abs(tempLng - markerLng) < EPS
            if (matchesById || matchesByPosition) {
              // Update color_id in temporary marker
              tempMarker.color_id = selectionStore.selectedColor
            }
          })
        }
      }

      // Reload saved markers from target group to ensure everything is in sync
      if (targetGroup) {
        await targetGroup.loadSavedMarkers()
      }
    } catch (err) {
      console.warn(err)
    } finally {
      this.processing = false
      selectionStore.hidePopupMenu()
      selectionStore._isEditing = false
    }
  }

  toggleSideBarMenu () {
    if (this.sideMenuHidden) {
      this.sideMenuHidden = false
    } else {
      this.sideMenuHidden = true
    }
  }

  _isSameMapItem (a, b, aGroupPk, bGroupPk) {
    if (!a || !b) return false
    if (a === b) return true

    // Check entity types: Marker vs Shape vs Boundary vs Group
    const aIsMarker = Boolean(a.layer === 'marker' || a.is_general !== undefined || a.latitude !== undefined || a.lat !== undefined)
    const bIsMarker = Boolean(b.layer === 'marker' || b.is_general !== undefined || b.latitude !== undefined || b.lat !== undefined)
    if (aIsMarker !== bIsMarker) return false

    const aIsBoundary = Boolean(a.layer === 'boundary' || a.code !== undefined || a.isZipCode !== undefined || (a.inner_id != null && a.remote_db_id != null))
    const bIsBoundary = Boolean(b.layer === 'boundary' || b.code !== undefined || b.isZipCode !== undefined || (b.inner_id != null && b.remote_db_id != null))
    if (aIsBoundary !== bIsBoundary) return false

    const aIsGroup = Boolean(a.pk != null && !a.targetGroupId && !a.layer && !aIsMarker && !aIsBoundary)
    const bIsGroup = Boolean(b.pk != null && !b.targetGroupId && !b.layer && !bIsMarker && !bIsBoundary)
    if (aIsGroup !== bIsGroup) return false

    if (aIsGroup && bIsGroup) {
      if (a.pk != null && b.pk != null && String(a.pk) === String(b.pk)) return true
      if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true
      return false
    }

    // If both belong to groups, ensure group matches
    const gA = aGroupPk || (a.targetGroupId ? String(a.targetGroupId) : null)
    const gB = bGroupPk || (b.targetGroupId ? String(b.targetGroupId) : null)
    if (gA && gB && gA !== gB) return false

    // Boundary comparison
    if (aIsBoundary) {
      if (a.inner_id != null && b.inner_id != null && String(a.inner_id) === String(b.inner_id)) return true
      if (a.remote_db_id != null && b.remote_db_id != null && String(a.remote_db_id) === String(b.remote_db_id)) return true
      if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true
      return false
    }

    // Shape / Marker comparison
    if (a.id != null && b.id != null && String(a.id) === String(b.id)) {
      if (gA && gB) return gA === gB
      return true
    }

    if (a.inner_id != null && b.inner_id != null && String(a.inner_id) === String(b.inner_id)) {
      if (gA && gB) return gA === gB
      return true
    }

    return false
  }

  isItemSelected (mapItem, group) {
    if (!mapItem) return false
    const item = selectionStore.item
    const groupPk = group ? String(group.pk) : (mapItem.targetGroupId ? String(mapItem.targetGroupId) : null)
    const selectedGroup = selectionStore.data && selectionStore.data.group
    const selectedGroupPk = selectedGroup ? String(selectedGroup.pk) : (item && item.targetGroupId ? String(item.targetGroupId) : null)

    if (selectionStore.selectedMenuItems && selectionStore.selectedMenuItems.length > 0) {
      return selectionStore.selectedMenuItems.some(selected => {
        const selGroupPk = selected.targetGroupId ? String(selected.targetGroupId) : selectedGroupPk
        return this._isSameMapItem(mapItem, selected, groupPk, selGroupPk)
      })
    }

    if (item) {
      return this._isSameMapItem(mapItem, item, groupPk, selectedGroupPk)
    }

    return false
  }

  filterByRange (value, rangeMin, rangeMax) {
    return value >= rangeMin && value <= rangeMax
  }

  filterByStatus (item) {
    const statusValue = this.filters.statusValue
    if (!statusValue) return true
    const status = dataStore.shapeStatuses.find(s => s.name === statusValue)
    return !!status && item.status === status.id
  }

  // An item matches when it carries at least one of the selected tags.
  filterByTags (item) {
    const tagIds = this.filters.tagIds
    if (tagIds.length === 0) return true
    return (item.tag_ids || []).some(id => tagIds.includes(id))
  }

  filterByAnalysis (analysis, region) {
    // Analysis data loads/reloads asynchronously (e.g. after switching the
    // data source), and RegionAnalysis fields are undefined while loading.
    // Don't filter a region out just because that data hasn't arrived yet.
    if (analysis && analysis.loading) {
      return true
    }

    const incomeFilter = this.filters.income || this.filters.hhSize
    let matchesIncome = true
    if (incomeFilter && !incomeFilter.isAtDefault && !incomeFilter.disabled) {
      let incomeValue = null
      if (analysis && analysis.hh_size != null) {
        incomeValue = analysis.hh_size
      }
      if (region && region.population_datas) {
        const picked = region.pickPopulationDataForCurrentYear ? region.pickPopulationDataForCurrentYear(region.population_datas) : (Array.isArray(region.population_datas) ? region.population_datas[0] : null)
        if (picked) {
          if (typeof picked.hh_size === 'number' && !isNaN(picked.hh_size)) {
            incomeValue = picked.hh_size
          }
        }
      }
      matchesIncome = this.filterByRange(incomeValue ?? 0, this.filters.incomeMin, this.filters.incomeMax)
    }

    let matchesPopulation = true
    if (this.filters.population && !this.filters.population.isAtDefault && !this.filters.population.disabled) {
      matchesPopulation = this.filterByRange(analysis.sum ?? 0, this.filters.populationMin, this.filters.populationMax)
    }

    let matchesDensity = true
    if (this.filters.density && !this.filters.density.isAtDefault && !this.filters.density.disabled) {
      matchesDensity = this.filterByRange(analysis.density ?? 0, this.filters.densityMin, this.filters.densityMax)
    }

    let matchesAvgAge = true
    if (this.filters.avgAge && !this.filters.avgAge.isAtDefault && !this.filters.avgAge.disabled) {
      matchesAvgAge = this.filterByRange(analysis.avgAge ?? 0, this.filters.avgAgeMin, this.filters.avgAgeMax)
    }

    let matchesGenderRatio = true
    if (this.filters.genderRatio && !this.filters.genderRatio.isAtDefault && !this.filters.genderRatio.disabled) {
      matchesGenderRatio = this.filterByRange(analysis.genderRatio ?? 0, this.filters.genderRatioMin, this.filters.genderRatioMax)
    }

    return (
      matchesPopulation &&
      matchesDensity &&
      matchesAvgAge &&
      matchesGenderRatio &&
      matchesIncome
    )
  }

  resetFilters () {
    if (this.filters) {
      this.filters.resetFilters()
    }
  }

  expandTargetGroups () {
    this.displayGroups.forEach(group => { group.expanded = true })
  }

  @action toggleHeatmap () {
    if (this.isDensityMapActive) {
      this.densityLayer = 'none'
    } else {
      const totalLayerObj = densityLayers.layers.find(item => item.name === 'Total')
      this.densityLayer = totalLayerObj.id
    }
  }

  @action.bound setHoveredMarker (id) {
    // Only one POI tooltip(marker or polygon) can be visible at a time
    this.hoveredPolygonId = null
    this.hoveredPolygonGroupId = null
    this.hoveredMarkerId = id
  }

  @action.bound clearHoveredMarker () {
    this.hoveredMarkerId = null
  }

  @action.bound setClickedMarker (id) {
    this.clickedMarkerId = id
  }

  @action.bound clearClickedMarker () {
    this.clickedMarkerId = null
  }

  @action.bound setHoveredMarkerForZIndex (id) {
    if (id === undefined || id === null) return
    this.hoveredMarkerIdsForZIndex.set(id, true)
  }

  @action.bound clearHoveredMarkerForZIndex (id) {
    if (id === undefined || id === null) return
    this.hoveredMarkerIdsForZIndex.delete(id)
  }

  @action.bound setHoveredPoiMarkerForAnimation (id) {
    this.hoveredPoiMarkerIdsForAnimation.set(id, true)
  }

  @action.bound clearHoveredPoiMarkerForAnimation (id) {
    this.hoveredPoiMarkerIdsForAnimation.delete(id)
  }

  @action.bound setHoveredPolygon (id, clickEvent, mapElement, groupId = null) {
    // Only one POI tooltip(marker or polygon) can be visible at a time
    this.hoveredMarkerId = null
    this.clickedMarkerId = null
    this.hoveredPolygonId = id
    this.hoveredPolygonGroupId = groupId
    if (clickEvent && mapElement) {
      const mapRect = mapElement.getBoundingClientRect()
      this.hoveredPolygonPosition = {
        top: clickEvent.clientY - mapRect.top,
        left: clickEvent.clientX - mapRect.left
      }
    } else {
      this.hoveredPolygonPosition = null
    }
  }

  @action.bound clearHoveredPolygon () {
    this.hoveredPolygonId = null
    this.hoveredPolygonGroupId = null
    this.hoveredPolygonPosition = null
  }

  // Register a polygon layer reference for direct manipulation
  registerPolygonLayer (polygonId, layerRef) {
    if (layerRef && layerRef.leafletElement) {
      this.layerRefs[polygonId] = layerRef
    }
  }

  // Unregister a polygon layer reference
  unregisterPolygonLayer (polygonId) {
    delete this.layerRefs[polygonId]
  }

  // Highlight a polygon on the map (for chart hover)
  highlightPolygonFromChart (polygonId, color) {
    const layer = this.layerRefs[polygonId]
    if (layer && layer.leafletElement) {
      const leafletLayer = layer.leafletElement
      // Store original style if not already stored
      if (!leafletLayer._originalStyle) {
        leafletLayer._originalStyle = {
          color: leafletLayer.options.color || '#3388ff',
          weight: leafletLayer.options.weight || 2,
          fillOpacity: leafletLayer.options.fillOpacity || 0.2
        }
      }
      // Apply highlight style
      leafletLayer.setStyle({
        color: color || '#ff7800',
        weight: 5,
        fillOpacity: 0.7
      })
      leafletLayer.bringToFront()
    }
  }

  // Unhighlight a polygon on the map (restore original style)
  unhighlightPolygonFromChart (polygonId) {
    const layer = this.layerRefs[polygonId]
    if (layer && layer.leafletElement) {
      const leafletLayer = layer.leafletElement
      if (leafletLayer._originalStyle) {
        leafletLayer.setStyle(leafletLayer._originalStyle)
        delete leafletLayer._originalStyle
      }
    }
  }

  @action.bound setHoveredTargetGroup (id, buttonElement, toolsElement, group = null) {
    if (selectionStore && selectionStore.menuItem && selectionStore.menuItem.visible) {
      return
    }
    if (this.showClickTooltip || this.showDeletePrompt || this.isAddChoiceTypeVisible) {
      return
    }
    this.cancelClearHoveredTargetGroup()
    this.hoveredTargetGroupId = id
    this.hoveredTargetGroup = group
    if (buttonElement && toolsElement) {
      this.updateHoveredTargetGroupPosition(buttonElement, toolsElement)
    } else {
      this.hoveredTargetGroupPosition = null
    }
  }

  @action.bound scheduleClearHoveredTargetGroup (delay = 250) {
    this.cancelClearHoveredTargetGroup()
    this._hoveredTargetGroupHideTimer = setTimeout(() => {
      this.clearHoveredTargetGroup()
    }, delay)
  }

  @action.bound cancelClearHoveredTargetGroup () {
    if (this._hoveredTargetGroupHideTimer) {
      clearTimeout(this._hoveredTargetGroupHideTimer)
      this._hoveredTargetGroupHideTimer = null
    }
  }

  @action.bound updateHoveredTargetGroupPosition (buttonElement, toolsElement) {
    if (!this.hoveredTargetGroupId) return
    if (!buttonElement || !toolsElement) {
      // Don't clear position if elements aren't found - they might just not be ready yet
      return
    }
    const buttonRect = buttonElement.getBoundingClientRect()
    const toolsRect = toolsElement.getBoundingClientRect()
    // Estimate tooltip height(target group tooltips can be 150-250px depending on content)
    const estimatedTooltipHeight = 220
    const margin = 15
    // Calculate viewport boundaries
    const viewportHeight = window.innerHeight
    const viewportBottom = viewportHeight
    // Position tooltip to the right of the button, vertically centered
    let top = buttonRect.top - toolsRect.top + buttonRect.height / 2
    // Calculate where the tooltip's bottom edge would be in viewport coordinates
    const tooltipBottomInViewport = toolsRect.top + top + (estimatedTooltipHeight / 2)
    // Check if tooltip would overflow at bottom of viewport
    if (tooltipBottomInViewport > viewportBottom - margin) {
      // Calculate maximum allowed top position relative to tools container
      const maxTop = (viewportBottom - margin) - toolsRect.top - (estimatedTooltipHeight / 2)
      // This ensures tooltip doesn't go below viewport but stays as close to button as possible
      top = Math.min(top, maxTop)
      // Ensure tooltip doesn't go above the tools container(with some margin)
      const minTop = estimatedTooltipHeight / 2 + margin
      top = Math.max(minTop, top)
    }
    const left = toolsRect.width + 10 // Position cleanly outside the left sidebar
    // Only update if values are valid numbers
    if (isFinite(top) && isFinite(left) && top >= 0 && left >= 0) {
      this.hoveredTargetGroupPosition = { top, left }
    }
  }

  @action.bound clearHoveredTargetGroup () {
    this.cancelClearHoveredTargetGroup()
    this.hoveredTargetGroupId = null
    this.hoveredTargetGroup = null
    this.hoveredTargetGroupPosition = null
  }

  @action.bound handlePoiTooltipOutsideClick (event) {
    const target = event.target
    if (!target || typeof target.closest !== 'function') return
    // Ignore clicks inside markers, polygons, POI tooltips, Pro save tooltips, or modals
    if (
      target.closest('.custom-map-marker') ||
      target.closest('.geopolygon') ||
      target.closest('.hotel-pin-container') ||
      target.closest('.poi-tooltip__container') ||
      target.closest('.search-results__save-button-wrapper') ||
      target.closest('.search-results__pro-tooltip') ||
      target.closest('.share-email-modal') ||
      target.closest('.share-image-modal') ||
      target.closest('.share-image-modal-overlay') ||
      target.closest('.kml-export-modal-overlay') ||
      target.closest('.action-feedback-toast-container')
    ) {
      return
    }
    this.clearHoveredMarker()
    this.clearClickedMarker()
    this.clearHoveredPolygon()
    this.showSaveProTooltip = false
    this.showSaveToFolderProTooltip = false
  }
}

export const uiStore = new UIStore()
